import type { ActionType } from "../actions";
import type { LocalizedText, ExecutionContext, Lang } from "../meta";

const ALL_CONTEXTS = [
    "entity_boot",
    "player_interact",
    "player_use",
] as const satisfies readonly ExecutionContext[];
const PLAYER_CONTEXTS = ["player_interact", "player_use"] as const satisfies readonly ExecutionContext[];
const ENTITY_CONTEXTS = ["entity_boot", "player_interact"] as const satisfies readonly ExecutionContext[];

export type ActionCategory =
    | "combat"
    | "economy"
    | "narrative"
    | "exploration"
    | "event"
    | "interface";

export type ActionContentOption = {
    field: string;
    label: LocalizedText;
    detail: LocalizedText;
};

export type ActionContent = {
    name: LocalizedText;
    desc: LocalizedText;
    when: LocalizedText;
    effect: LocalizedText;
    options: readonly ActionContentOption[];
    related: readonly ActionType[];
};

export type ActionPickerExposure = "public" | "advanced" | "internal" | "deprecated";

export type ActionPickerMeta = {
    exposure: ActionPickerExposure;
    keywords: { en: readonly string[]; fr: readonly string[] };
    priority?: number;
};

export type ActionMeta = {
    type: ActionType;
    icon: string;
    category: ActionCategory;
    validContexts: readonly ExecutionContext[];
    requiresPlayer: boolean;
    requiresEntity: boolean;
    requiresSourceItem: boolean;
    isBlocking: boolean;
    skippedWhenMissingContext: boolean;
    picker?: ActionPickerMeta;
    content: ActionContent;
};

export const ACTION_META: readonly ActionMeta[] = [
    {
        type: "ChangeBehavior",
        icon: "/icons/behavior.svg",
        category: "combat",
        validContexts: ENTITY_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: {
                en: "Change behavior",
                fr: "Changer le comportement",
            },
            desc: {
                en: "Changes how the entity moves or fights. Turns any entity into a threat, a protector or a wanderer — Aggressive mode launches a full combat encounter.",
                fr: "Change comment l'entité se déplace ou combat. Transforme n'importe quelle entité en menace, en protecteur ou en vagabond — le mode Agressif lance un vrai combat.",
            },
            when: {
                en: "Boss encounters, NPCs turning hostile, patrol-to-combat transitions, taming mechanics.",
                fr: "Combats de boss, PNJ qui deviennent hostiles, transitions patrouille-combat, mécaniques d'apprivoisement.",
            },
            options: [
                {
                    field: "behaviorType",
                    label: { en: "Behavior type", fr: "Type de comportement" },
                    detail: { en: "Static, Move, Protect or Aggressive.", fr: "Statique, Mobile, Protéger ou Agressif." },
                },
                {
                    field: "targetItemId",
                    label: { en: "Protected target", fr: "Cible protégée" },
                    detail: {
                        en: "Player protects the owner; any other item protects the owner's matching world item.",
                        fr: "Player protège le propriétaire ; tout autre item protège l'item correspondant possédé.",
                    },
                },
                {
                    field: "targetItemIds",
                    label: { en: "Aggressive targets", fr: "Cibles agressives" },
                    detail: {
                        en: "Item types this entity may attack. The owner and the owner's items are always excluded.",
                        fr: "Types d'items que cette entité peut attaquer. Le propriétaire et ses items sont toujours exclus.",
                    },
                },
            ],
            effect: {
                en: "The entity switches behavior instantly. Protect mode guards the selected owned target while ignoring the owner side.",
                fr: "L'entité change de comportement immédiatement. Le mode Protéger défend la cible possédée sélectionnée en ignorant son camp.",
            },
            related: ["Despawn", "Dialogue", "Spawn"],
        },
    },
    {
        type: "ModifyStat",
        icon: "/icons/stat-modify.svg",
        category: "combat",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Modify stat", fr: "Modifier une stat" },
            desc: {
                en: "Changes a stat value (health, attack…). Heal, damage, buff or drain any statistic in a single action.",
                fr: "Change la valeur d'une stat (vie, attaque…). Soigne, inflige des dégâts, buff ou draine n'importe quelle statistique en une seule action.",
            },
            when: {
                en: "Health potions, durability loss, hunger management, experience gains, stat buffs.",
                fr: "Potions de soin, usure de durabilité, gestion de faim, gains d'expérience, buffs de stats.",
            },
            options: [
                {
                    field: "mode",
                    label: { en: "Mode", fr: "Mode" },
                    detail: { en: "Restore, Increase, Decrease or Set.", fr: "Restaurer, Augmenter, Diminuer ou Définir." },
                },
                {
                    field: "statKey",
                    label: { en: "Stat", fr: "Stat" },
                    detail: { en: "Which stat to modify (vitality, hunger…).", fr: "Quelle stat modifier (vitalité, faim…)." },
                },
                {
                    field: "value",
                    label: { en: "Amount", fr: "Valeur" },
                    detail: { en: "How much to change — flat number or percentage.", fr: "De combien changer — nombre fixe ou pourcentage." },
                },
                {
                    field: "target",
                    label: { en: "Target", fr: "Cible" },
                    detail: {
                        en: "Defaults to the current actor. Use $event.target to damage a detected player or item.",
                        fr: "Par défaut, cible l'acteur courant. Utilise $event.target pour toucher un joueur ou item détecté.",
                    },
                },
            ],
            effect: {
                en: "The value changes immediately, clamped within the min/max range defined on the stat.",
                fr: "La valeur change immédiatement, plafonnée aux limites min/max de la stat.",
            },
            related: ["ConsumeItem", "Branch"],
        },
    },
    {
        type: "ToggleEquip",
        icon: "/icons/equip.svg",
        category: "combat",
        validContexts: ["player_use"],
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: true,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Equip / Unequip", fr: "Équiper / Déséquiper" },
            desc: {
                en: "Toggles the item's equipped state. The engine manages slots, visual attachment and companion spawning automatically.",
                fr: "Bascule l'état équipé de l'item. Le moteur gère les emplacements, l'attachement visuel et l'apparition de compagnons automatiquement.",
            },
            when: {
                en: "Weapons, armor, pets, accessories — any equippable item in your game.",
                fr: "Armes, armures, familiers, accessoires : tout objet équipable de votre jeu.",
            },
            options: [],
            effect: {
                en: "Toggles the equip state. World items spawn followers, hand items attach to the character.",
                fr: "Bascule l'état d'équipement. Les objets Monde font apparaître des compagnons, les objets Main s'attachent au personnage.",
            },
            related: ["ConsumeItem", "ModifyItem", "ChangeBehavior"],
        },
    },
    {
        type: "DeployItem",
        icon: "/icons/spawn.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: true,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Deploy item", fr: "Deployer l'item" },
            desc: {
                en: "Places an owned item instance into the world without treating it as equipped gear.",
                fr: "Place une instance d'item possedee dans le monde sans la traiter comme un equipement.",
            },
            when: {
                en: "Workers, companions, guards, puzzle objects and any inventory item that should act in the world.",
                fr: "Travailleurs, compagnons, gardes, objets de puzzle et tout item d'inventaire qui agit dans le monde.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Item instance", fr: "Instance d'item" },
                    detail: { en: "Optional. Defaults to the current inventory item.", fr: "Optionnel. Par defaut, l'item courant." },
                },
            ],
            effect: {
                en: "The item is marked Deployed and its world model is spawned or repositioned.",
                fr: "L'item passe en Deployed et son modele monde apparait ou se repositionne.",
            },
            related: ["RecallItem", "ChangeBehavior", "Interact"],
        },
    },
    {
        type: "RecallItem",
        icon: "/icons/despawn.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Recall item", fr: "Ranger l'item" },
            desc: {
                en: "Removes a deployed item instance from the world and returns it to inventory storage.",
                fr: "Retire une instance d'item deployee du monde et la remet en stockage d'inventaire.",
            },
            when: {
                en: "Recall buttons, companion menus, worker cancellation and puzzle reset flows.",
                fr: "Boutons de rappel, menus de compagnon, annulation de travail et remise a zero de puzzle.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Item instance", fr: "Instance d'item" },
                    detail: { en: "Optional. Defaults to the current deployed item or source item.", fr: "Optionnel. Par defaut, l'item deploye courant ou l'item source." },
                },
            ],
            effect: {
                en: "The deployed model is destroyed, movement/combat behavior stops, and the item is marked Stored.",
                fr: "Le modele deploye est detruit, le mouvement/combat s'arrete, et l'item repasse en Stored.",
            },
            related: ["DeployItem", "Despawn", "ChangeBehavior"],
        },
    },
    {
        type: "PlaceItem",
        icon: "/icons/spawn.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Place item", fr: "Placer l'item" },
            desc: {
                en: "Opens a build-anchor selector whose placeable items are derived from the allowed recipes' item outputs, then places the owned or crafted item on that anchor.",
                fr: "Ouvre un selecteur d'ancre de construction dont les items posables sont deduits des sorties-item des recettes autorisees, puis pose l'item possede ou fabrique sur cette ancre.",
            },
            when: {
                en: "Doors, turrets, decorations, robots and base modules placed on authored Studio anchors.",
                fr: "Portes, tourelles, decorations, robots et modules de base poses sur des ancres Studio.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Anchor", fr: "Ancre" },
                    detail: { en: "Optional. Defaults to the current entity/model.", fr: "Optionnel. Par defaut, l'entite ou le modele courant." },
                },
                {
                    field: "filter",
                    label: { en: "Category filter", fr: "Filtre categorie" },
                    detail: { en: "Optional item category accepted by this anchor.", fr: "Categorie d'item optionnelle acceptee par cette ancre." },
                },
                {
                    field: "recipes",
                    label: { en: "Allowed recipes", fr: "Recettes autorisees" },
                    detail: { en: "Required (at least one). The placeable items are the item outputs of these recipes: an owned output is offered directly, otherwise the recipe's craft/buy cost applies.", fr: "Obligatoire (au moins une). Les items posables sont les sorties-item de ces recettes : une sortie possedee est proposee directement, sinon le cout craft/achat de la recette s'applique." },
                },
            ],
            effect: {
                en: "The chosen item becomes Deployed and records the anchor runtime id. Any previous item on the anchor is returned to inventory.",
                fr: "L'item choisi passe en Deployed et garde l'id runtime de l'ancre. L'ancien item de l'ancre retourne dans l'inventaire.",
            },
            related: ["DeployItem", "RecallItem", "SelectTargetItem"],
        },
    },
    {
        type: "ClaimPlace",
        icon: "/icons/flag.svg",
        category: "exploration",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Claim place", fr: "Revendiquer le lieu" },
            desc: {
                en: "Assigns one available claimable place group to the player.",
                fr: "Attribue au joueur un lieu disponible dans un groupe revendiquable.",
            },
            when: {
                en: "Player houses, bases, private stations or any shared map spot that becomes owned.",
                fr: "Maisons, bases, ateliers privés ou tout emplacement du monde qui devient possédé.",
            },
            options: [
                {
                    field: "placeGroupId",
                    label: { en: "Place group", fr: "Groupe de lieux" },
                    detail: { en: "Hidden id generated from the child-facing place label.", fr: "Id caché généré depuis le libellé visible du lieu." },
                },
            ],
            effect: {
                en: "The claim is persisted on the player and later zone resolution filters to that place.",
                fr: "La revendication est sauvegardée sur le joueur et la résolution des zones filtre ensuite ce lieu.",
            },
            related: ["GoToZone"],
        },
    },
    {
        type: "GoToZone",
        icon: "/icons/move.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Go to zone", fr: "Aller en zone" },
            desc: {
                en: "Sends a deployed item instance to a linked Zone item, optionally constrained to a place group.",
                fr: "Envoie une instance d'item déployée vers un item Zone lié, avec un groupe de lieux optionnel.",
            },
            when: {
                en: "Workers gathering resources, guards taking a post, objects occupying puzzle sockets.",
                fr: "Travailleurs qui récoltent, gardes en poste, objets qui occupent des emplacements de puzzle.",
            },
            options: [
                {
                    field: "zoneItemId",
                    label: { en: "Zone item", fr: "Item Zone" },
                    detail: { en: "The linked Zone item to occupy.", fr: "L'item Zone lié à occuper." },
                },
                {
                    field: "placeGroupId",
                    label: { en: "Where", fr: "Où" },
                    detail: { en: "Optional when the Zone item exists in more than one place group.", fr: "Optionnel quand l'item Zone existe dans plusieurs groupes de lieux." },
                },
                {
                    field: "target",
                    label: { en: "Item instance", fr: "Instance d'item" },
                    detail: { en: "Optional. Defaults to the current deployed item or source item.", fr: "Optionnel. Par défaut, l'item déployé courant ou l'item source." },
                },
            ],
            effect: {
                en: "Emits Arrived, NoWay for normal blocks, or Failed for broken data/runtime problems.",
                fr: "Émet Arrived, NoWay pour les blocages normaux, ou Failed si une donnée/runtime est cassée.",
            },
            related: ["StartProduction", "ChangeBehavior", "RecallItem"],
        },
    },
    {
        type: "GoToItem",
        icon: "/icons/move.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Go to item", fr: "Aller vers item" },
            desc: {
                en: "Sends a deployed item instance to the nearest linked item model, optionally constrained to a place group.",
                fr: "Envoie une instance d'item déployée vers le modèle d'item lié le plus proche, avec un groupe de lieux optionnel.",
            },
            when: {
                en: "Workers walking to machines, guards moving to a door, objects targeting another object.",
                fr: "Travailleurs allant vers des machines, gardes vers une porte, objets ciblant un autre objet.",
            },
            options: [
                {
                    field: "itemId",
                    label: { en: "Target item", fr: "Item cible" },
                    detail: { en: "The linked item to walk toward.", fr: "L'item lié vers lequel marcher." },
                },
                {
                    field: "placeGroupId",
                    label: { en: "Where", fr: "Où" },
                    detail: { en: "Optional when the target item exists in more than one place group.", fr: "Optionnel quand l'item cible existe dans plusieurs groupes de lieux." },
                },
                {
                    field: "target",
                    label: { en: "Moving item", fr: "Item mobile" },
                    detail: { en: "Optional. Defaults to the current deployed item or source item.", fr: "Optionnel. Par défaut, l'item déployé courant ou l'item source." },
                },
            ],
            effect: {
                en: "Emits Arrived, NoWay for normal blocks, or Failed for broken data/runtime problems.",
                fr: "Émet Arrived, NoWay pour les blocages normaux, ou Failed si une donnée/runtime est cassée.",
            },
            related: ["GoToZone", "ChangeBehavior", "PlaceItem"],
        },
    },
    {
        type: "LeaveZone",
        icon: "/icons/move.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Leave zone", fr: "Quitter la zone" },
            desc: {
                en: "Releases the current Zone occupied by an item instance and emits the zone exit events.",
                fr: "Libère la Zone occupée par une instance d'item et déclenche les événements de sortie de zone.",
            },
            when: {
                en: "Stop a worker task, free a production spot, or cancel an item assigned to a zone.",
                fr: "Arrêter une tâche de compagnon, libérer un spot de production ou annuler un item affecté à une zone.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Item instance", fr: "Instance d'item" },
                    detail: { en: "Optional. Defaults to the current deployed item or source item.", fr: "Optionnel. Par défaut, l'item déployé courant ou l'item source." },
                },
            ],
            effect: {
                en: "The item is no longer considered inside that Zone. OnExit and OnZoneExit graphs run immediately.",
                fr: "L'item n'est plus considéré dans cette Zone. Les graphes OnExit et OnZoneExit se lancent immédiatement.",
            },
            related: ["GoToZone", "StopProduction", "ChangeBehavior"],
        },
    },
    {
        type: "StartProduction",
        icon: "/icons/timer.svg",
        category: "economy",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Start production", fr: "Démarrer la production" },
            desc: {
                en: "Stores production state on an owned item instance.",
                fr: "Stocke l'état de production sur une instance d'item possédée.",
            },
            when: {
                en: "A deployed item enters a work spot and begins generating items or currency.",
                fr: "Un item déployé entre dans un emplacement et commence à générer items ou monnaie.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Producer", fr: "Producteur" },
                    detail: { en: "Optional. Defaults to the entering item.", fr: "Optionnel. Par défaut, l'item entrant." },
                },
                {
                    field: "outputs",
                    label: { en: "Outputs", fr: "Sorties" },
                    detail: { en: "Items or currencies generated per hour.", fr: "Items ou monnaies générés par heure." },
                },
            ],
            effect: {
                en: "The producer accumulates outputs until stopped or collected.",
                fr: "Le producteur accumule les sorties jusqu'à l'arrêt ou la collecte.",
            },
            related: ["GoToZone", "CollectProduction", "StopProduction"],
        },
    },
    {
        type: "StopProduction",
        icon: "/icons/stop.svg",
        category: "economy",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Stop production", fr: "Arrêter la production" },
            desc: {
                en: "Stops the production state on an owned item instance.",
                fr: "Arrête l'état de production sur une instance d'item possédée.",
            },
            when: {
                en: "When an item leaves a spot, is recalled, or changes role.",
                fr: "Quand un item quitte une zone, est rangé, ou change de rôle.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Producer", fr: "Producteur" },
                    detail: { en: "Optional. Defaults to the current item.", fr: "Optionnel. Par défaut, l'item courant." },
                },
            ],
            effect: {
                en: "Accumulation pauses. Previously accumulated amounts remain collectible.",
                fr: "L'accumulation s'arrête. Les montants déjà accumulés restent collectables.",
            },
            related: ["StartProduction", "CollectProduction"],
        },
    },
    {
        type: "CollectProduction",
        icon: "/icons/collect.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Collect production", fr: "Collecter la production" },
            desc: {
                en: "Collects stored outputs from one producer, all producers, or an explicit target.",
                fr: "Collecte les sorties stockées d'un producteur, de tous les producteurs, ou d'une cible explicite.",
            },
            when: {
                en: "Chest buttons, item interaction, harvest all workflows.",
                fr: "Boutons de coffre, interaction d'item, parcours tout récupérer.",
            },
            options: [
                {
                    field: "mode",
                    label: { en: "Mode", fr: "Mode" },
                    detail: { en: "Self, All, or Target.", fr: "Self, All, ou Target." },
                },
                {
                    field: "target",
                    label: { en: "Target", fr: "Cible" },
                    detail: { en: "Used when mode is Target.", fr: "Utilisé quand le mode est Target." },
                },
            ],
            effect: {
                en: "Outputs are granted to the player and collected producer clocks reset.",
                fr: "Les sorties sont données au joueur et les horloges collectées sont remises à zéro.",
            },
            related: ["GetAllProductions", "StartProduction"],
        },
    },
    {
        type: "GetAllProductions",
        icon: "/icons/filter.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "All productions", fr: "Toutes les productions" },
            desc: {
                en: "Reads every active production owned by the player, with per-producer rows and grouped totals.",
                fr: "Lit toutes les productions du joueur, avec les lignes par producteur et les totaux regroupes.",
            },
            when: {
                en: "Chest interfaces, worker dashboards, harvest-all buttons and production summaries.",
                fr: "Interfaces de coffre, tableaux de travailleurs, boutons tout recolter et resumes de production.",
            },
            options: [],
            effect: {
                en: "Outputs `items`, `totals` and `count` through data ports.",
                fr: "Expose `items`, `totals` et `count` via les ports de donnees.",
            },
            related: ["CollectProduction", "StartProduction"],
        },
    },
    {
        type: "Despawn",
        icon: "/icons/despawn.svg",
        category: "combat",
        validContexts: ENTITY_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Despawn", fr: "Faire disparaître" },
            desc: {
                en: "Removes the entity from the world. All pending action chains are cleaned up automatically.",
                fr: "Supprime l'entité du monde. Toutes les chaînes d'actions en attente sont nettoyées automatiquement.",
            },
            when: {
                en: "After defeating a boss, collecting a pickup, or when an NPC has finished its role.",
                fr: "Après un boss vaincu, un collectible ramassé, ou quand un PNJ a terminé son rôle.",
            },
            options: [],
            effect: {
                en: "The entity is destroyed and removed from the game world instantly.",
                fr: "L'entité est détruite et retirée du monde de jeu instantanément.",
            },
            related: ["ChangeBehavior", "Spawn"],
        },
    },
    {
        type: "Stop",
        icon: "/icons/stop.svg",
        category: "event",
        validContexts: ENTITY_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Stop", fr: "Arrêter" },
            desc: {
                en: "Terminator action. Cuts the current chain and disarms upstream rearming sources (Interact, OnProximity) on this entity instance. The instance keeps living but its graph branches no longer re-trigger.",
                fr: "Action terminale. Coupe la chaîne courante et désarme les sources réamorçables en amont (Interact, OnProximity) pour cette instance. L'instance continue d'exister mais les branches de son graphe ne se redéclenchent plus.",
            },
            when: {
                en: "Place at the end of a branch when this instance should not react again — e.g. a boss that becomes hostile after a dialogue, an NPC whose quest is finished.",
                fr: "À placer en bout de branche quand cette instance ne doit plus réagir — par ex. un boss qui devient hostile après un dialogue, un PNJ dont la quête est terminée.",
            },
            options: [],
            effect: {
                en: "The chain stops at this node. Interact prompts and OnProximity watchers placed earlier in the chain are unregistered. A new instance of the same template (e.g. after respawn) starts fresh.",
                fr: "La chaîne s'arrête ici. Les prompts Interact et watchers OnProximity placés en amont sont désinscrits. Une nouvelle instance du même template (par ex. après respawn) repart avec un état frais.",
            },
            related: ["Interact", "OnProximity", "ChangeBehavior", "Despawn"],
        },
    },
    {
        type: "Spawn",
        icon: "/icons/spawn.svg",
        category: "combat",
        validContexts: ["entity_boot"],
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Spawn (spawner)", fr: "Apparition (spawner)" },
            desc: {
                en: "Spawns entities in a loop. Control population limits and respawn timing to populate your world.",
                fr: "Fait apparaître des entités en boucle. Contrôle la population et le rythme pour peupler ton monde.",
            },
            when: {
                en: "Resource nodes, enemy waves, collectible respawning, populating game zones.",
                fr: "Noeuds de ressources, vagues d'ennemis, réapparition de collectibles, peuplement de zones.",
            },
            options: [
                {
                    field: "maxActive",
                    label: { en: "Max active", fr: "Maximum actif" },
                    detail: { en: "Maximum entities alive at the same time.", fr: "Nombre maximum d'entités vivantes en même temps." },
                },
                {
                    field: "interval",
                    label: { en: "Interval (s)", fr: "Intervalle (s)" },
                    detail: { en: "Seconds between each spawn.", fr: "Secondes entre chaque apparition." },
                },
                {
                    field: "contentList",
                    label: { en: "Content list", fr: "Liste de contenu" },
                    detail: { en: "Which entities to spawn — picked randomly from the list.", fr: "Quelles entités faire apparaître — choix aléatoire dans la liste." },
                },
            ],
            effect: {
                en: "Entities spawn up to the limit. In respawn mode, defeated entities are replaced after the interval.",
                fr: "Les entités apparaissent jusqu'à la limite. En mode réapparition, les entités vaincues sont remplacées après l'intervalle.",
            },
            related: ["Despawn", "ChangeBehavior", "Interact"],
        },
    },
    {
        type: "ModifyItem",
        icon: "/icons/add-item.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        picker: {
            exposure: "public",
            priority: 100,
            keywords: {
                en: ["give", "item", "loot", "drop", "inventory", "bag", "reward", "remove", "set"],
                fr: ["donner", "objet", "item", "butin", "drop", "inventaire", "sac", "recompense", "retirer"],
            },
        },
        content: {
            name: { en: "Modify inventory", fr: "Modifier l'inventaire" },
            desc: {
                en: "Adds, removes or sets how many copies of a catalog item the resolved owner has.",
                fr: "Ajoute, retire ou fixe combien d'exemplaires d'un item du catalogue le propriétaire résolu possède.",
            },
            when: {
                en: "Thieves stealing an item, penalties that take resources away, rewards that grant several copies at once, syncing a fixed amount after an event.",
                fr: "Un voleur qui vole un objet, une pénalité qui retire des ressources, une récompense qui offre plusieurs exemplaires d'un coup, synchroniser une quantité fixe après un événement.",
            },
            options: [
                {
                    field: "mode",
                    label: { en: "Mode", fr: "Mode" },
                    detail: {
                        en: "Add: gives more copies. Remove: takes copies away. Set: forces the exact amount the player should hold.",
                        fr: "Ajouter : donne des exemplaires en plus. Retirer : en enlève. Définir : force la quantité exacte à posséder.",
                    },
                },
                {
                    field: "itemId",
                    label: { en: "Item", fr: "Item" },
                    detail: { en: "Which item is affected.", fr: "Quel item est concerné." },
                },
                {
                    field: "quantity",
                    label: { en: "Quantity", fr: "Quantité" },
                    detail: {
                        en: "How many copies to add, remove, or reach.",
                        fr: "Combien d'exemplaires à ajouter, retirer, ou atteindre.",
                    },
                },
            ],
            effect: {
                en: "Writes to the player's inventory. Set 0 clears this item for the player.",
                fr: "Écrit dans l'inventaire du joueur. Définir 0 vide cet item pour le joueur.",
            },
            related: ["ModifyItem", "ConsumeItem", "ModifyCurrency"],
        },
    },
    {
        type: "AddRandomItem",
        icon: "/icons/random-item.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Give random item", fr: "Donner un item aléatoire" },
            desc: {
                en: "Gives a random item from a list. Build loot tables with weighted drop chances and rarity colors.",
                fr: "Donne un item au hasard parmi une liste. Crée des tables de butin avec probabilités pondérées et couleurs de rareté.",
            },
            when: {
                en: "Gacha mechanics, mystery crates, enemy drops, treasure chests with random rewards.",
                fr: "Mécaniques de gacha, caisses mystères, drops d'ennemis, coffres avec récompenses aléatoires.",
            },
            options: [
                {
                    field: "entries",
                    label: { en: "Loot table", fr: "Table de butin" },
                    detail: { en: "List of items with drop chances and optional rarity colors.", fr: "Liste d'items avec chances de drop et couleurs de rareté." },
                },
            ],
            effect: {
                en: "The engine rolls against weighted chances. A reward popup reveals the result with a rarity glow, then the item goes to inventory.",
                fr: "Le moteur tire selon les probabilités. Un popup révèle le résultat avec un halo de rareté, puis l'item va en inventaire.",
            },
            related: ["ModifyItem", "ConsumeItem", "Branch"],
        },
    },
    {
        type: "ConsumeItem",
        icon: "/icons/consume-item.svg",
        category: "economy",
        validContexts: ["player_use"],
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: true,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Consume item", fr: "Consommer l'item" },
            desc: {
                en: "Removes the used item from inventory. Perfect for consumables like food, potions, keys or single-use tools.",
                fr: "Supprime l'item utilisé de l'inventaire. Idéal pour les consommables : nourriture, potions, clés, outils à usage unique.",
            },
            when: {
                en: "Eating food, using a key, sacrificing ingredients, activating single-use items.",
                fr: "Manger, utiliser une clé, sacrifier des ingrédients, activer des objets à usage unique.",
            },
            options: [],
            effect: {
                en: "The item is permanently removed from the player's inventory after the action chain completes.",
                fr: "L'item est définitivement supprimé de l'inventaire du joueur après l'exécution de la chaîne.",
            },
            related: ["ModifyItem", "ModifyStat", "ToggleEquip"],
        },
    },
    {
        type: "ModifyCurrency",
        icon: "/icons/currency.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Modify currency", fr: "Modifier une devise" },
            desc: {
                en: "Adds, removes or sets a catalog currency on the resolved owner.",
                fr: "Ajoute, retire ou définit une devise du catalogue sur le propriétaire résolu.",
            },
            when: {
                en: "Shop purchases, quest rewards, entrance fees, selling items, salary systems.",
                fr: "Achats en boutique, récompenses de quête, frais d'entrée, vente d'items, systèmes de salaire.",
            },
            options: [
                {
                    field: "mode",
                    label: { en: "Mode", fr: "Mode" },
                    detail: { en: "Add, Remove or Set.", fr: "Ajouter, Retirer ou Définir." },
                },
                {
                    field: "currencyId",
                    label: { en: "Currency", fr: "Devise" },
                    detail: { en: "Which currency to modify.", fr: "Quelle devise modifier." },
                },
                {
                    field: "value",
                    label: { en: "Amount", fr: "Valeur" },
                    detail: { en: "How much — flat number or percentage.", fr: "Combien — nombre fixe ou pourcentage." },
                },
                {
                    field: "target",
                    label: { en: "Target", fr: "Cible" },
                    detail: {
                        en: "Defaults to player. $event.target modifies that player, or the owner wallet for an owned item.",
                        fr: "Par défaut : player. $event.target modifie ce joueur, ou le wallet propriétaire d'un item possédé.",
                    },
                },
            ],
            effect: {
                en: "Writes to the player's wallet. Values are never removed below zero.",
                fr: "Écrit dans le portefeuille du joueur. Les valeurs ne descendent jamais sous zéro.",
            },
            related: ["ModifyItem", "Branch", "Transform"],
        },
    },
    {
        type: "ExecuteTransaction",
        icon: "/icons/currency.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Execute transaction", fr: "Executer une transaction" },
            desc: {
                en: "Runs one recipe immediately: consumes its inputs and grants its outputs, or opens the Roblox purchase prompt for a developer product recipe.",
                fr: "Lance une recette immediatement : consomme ses entrees et donne ses sorties, ou ouvre l'achat Roblox pour une recette developer product.",
            },
            when: {
                en: "One-click purchases, tycoon buttons, upgrades, doors, turrets and paid unlocks.",
                fr: "Achats en un clic, boutons tycoon, upgrades, portes, tourelles et debloquages payants.",
            },
            options: [
                {
                    field: "recipeId",
                    label: { en: "Recipe", fr: "Recette" },
                    detail: { en: "The recipe to execute.", fr: "La recette a executer." },
                },
            ],
            effect: {
                en: "On success, inputs are spent atomically and outputs are granted. Developer product recipes open the Roblox payment prompt; the receipt grants the outputs later.",
                fr: "En succes, les entrees sont depensees atomiquement et les sorties sont donnees. Les recettes developer product ouvrent le paiement Roblox; le recu donne les sorties ensuite.",
            },
            related: ["Transform", "ModifyCurrency", "ModifyItem"],
        },
    },
    {
        type: "Transform",
        icon: "/icons/transform.svg",
        category: "economy",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Transform (exchange)", fr: "Transformer (échange)" },
            desc: {
                en: "Opens an exchange / craft menu for the player. Players combine resources into new items via defined recipes.",
                fr: "Ouvre un menu d'échange / craft pour le joueur. Les joueurs combinent des ressources en nouveaux items via des recettes définies.",
            },
            when: {
                en: "Crafting stations, cooking pots, forges, alchemy tables, workbenches.",
                fr: "Stations de craft, marmites, forges, tables d'alchimie, établis.",
            },
            options: [
                {
                    field: "recipes",
                    label: { en: "Recipes", fr: "Recettes" },
                    detail: { en: "Which recipes are available at this station.", fr: "Quelles recettes sont disponibles à cette station." },
                },
                {
                    field: "title",
                    label: { en: "Station name", fr: "Nom de la station" },
                    detail: { en: "Name displayed at the top of the crafting window.", fr: "Nom affiché en haut de la fenêtre de craft." },
                },
            ],
            effect: {
                en: "A crafting UI opens listing available recipes with required inputs and outputs.",
                fr: "Une interface de craft s'ouvre avec les recettes disponibles, leurs entrées et leurs sorties.",
            },
            related: ["Interact", "ConsumeItem", "ModifyItem"],
        },
    },
    {
        type: "SetTimer",
        icon: "/icons/timer.svg",
        category: "economy",
        validContexts: ["player_use"],
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: true,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Start timer", fr: "Lancer un timer" },
            desc: {
                en: "Starts a countdown on the item. The following actions execute when the timer expires. Add an optional skip cost for instant completion.",
                fr: "Démarre un compte à rebours sur l'item. Les actions suivantes s'exécutent à l'expiration. Ajoute un coût optionnel pour compléter instantanément.",
            },
            when: {
                en: "Crafting timers, plant growth, cooldowns, timed upgrades, incubation periods.",
                fr: "Minuteurs de craft, croissance de plantes, temps de recharge, améliorations chronométrées, périodes d'incubation.",
            },
            options: [
                {
                    field: "duration",
                    label: { en: "Duration (seconds)", fr: "Durée (secondes)" },
                    detail: { en: "How long the timer runs.", fr: "Combien de temps dure le timer." },
                },
                {
                    field: "skipCurrencyId",
                    label: { en: "Skip currency", fr: "Devise pour accélérer" },
                    detail: { en: "Currency accepted to complete instantly.", fr: "Devise acceptée pour compléter instantanément." },
                },
                {
                    field: "skipAmount",
                    label: { en: "Skip cost", fr: "Coût pour accélérer" },
                    detail: { en: "Amount to pay for instant completion.", fr: "Montant à payer pour compléter instantanément." },
                },
            ],
            effect: {
                en: "The item enters processing state. A countdown shows in the UI. Players can pay to complete instantly.",
                fr: "L'item passe en traitement. Un compte à rebours s'affiche. Les joueurs peuvent payer pour compléter instantanément.",
            },
            related: ["ModifyStat", "Transform", "ConsumeItem"],
        },
    },
    {
        type: "Dialogue",
        icon: "/icons/dialogue.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Dialogue", fr: "Dialogue" },
            desc: {
                en: "Shows dialogue bubbles. Pauses the action chain until the player finishes reading.",
                fr: "Affiche des bulles de dialogue. Met la chaîne en pause jusqu'à la fin de la lecture.",
            },
            when: {
                en: "NPC conversations, story sequences, tutorials, quest briefings, lore reveals.",
                fr: "Conversations de PNJ, séquences narratives, tutoriels, briefings de quête, révélations de lore.",
            },
            options: [],
            effect: {
                en: "A dialogue UI appears with the lines defined on the node. The chain resumes when the player finishes.",
                fr: "Une interface de dialogue s'affiche avec les répliques définies sur le node. La chaîne reprend quand le joueur a terminé.",
            },
            related: ["Interact", "Notify", "Branch"],
        },
    },
    {
        type: "Interact",
        icon: "/icons/interact.svg",
        category: "narrative",
        validContexts: ["entity_boot"],
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Interaction", fr: "Interaction" },
            desc: {
                en: "Pauses the chain. The player must press the action button to continue. Places a hold-E prompt on the entity.",
                fr: "Met la chaîne en pause. Le joueur doit appuyer sur le bouton d'action pour continuer. Place un bouton maintenir-E sur l'entité.",
            },
            when: {
                en: "Talking to NPCs, harvesting resources, opening chests, activating mechanisms, picking up items.",
                fr: "Parler aux PNJ, récolter des ressources, ouvrir des coffres, activer des mécanismes, ramasser des items.",
            },
            options: [
                {
                    field: "label",
                    label: { en: "Prompt label", fr: "Texte du bouton" },
                    detail: { en: "Text shown on the interaction prompt.", fr: "Texte affiché sur le bouton d'interaction." },
                },
            ],
            effect: {
                en: "A prompt appears above the entity. The player holds E, then the next action fires.",
                fr: "Un bouton apparaît au-dessus de l'entité. Le joueur maintient E, puis l'action suivante se déclenche.",
            },
            related: ["Dialogue", "ModifyItem", "ChangeBehavior"],
        },
    },
    {
        type: "Notify",
        icon: "/icons/notify.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Notification", fr: "Notification" },
            desc: {
                en: "Shows a message on the player's screen. Choose between success, error, warning and info styles.",
                fr: "Affiche un message à l'écran du joueur. Choisis entre les styles succès, erreur, avertissement et info.",
            },
            when: {
                en: "Quest updates, purchase confirmations, error feedback, system messages, achievement alerts.",
                fr: "Mises à jour de quête, confirmations d'achat, retours d'erreur, messages système, alertes de succès.",
            },
            options: [
                {
                    field: "notifType",
                    label: { en: "Style", fr: "Type" },
                    detail: { en: "Success, Error, Warning or Info.", fr: "Succès, Erreur, Avertissement ou Info." },
                },
                {
                    field: "message",
                    label: { en: "Message", fr: "Message" },
                    detail: { en: "The notification text to display.", fr: "Le texte de notification à afficher." },
                },
            ],
            effect: {
                en: "A styled toast notification appears briefly on screen.",
                fr: "Une notification stylée apparaît brièvement à l'écran.",
            },
            related: ["Dialogue", "SetGoal"],
        },
    },
    {
        type: "SetGoal",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Guide player", fr: "Guider le joueur" },
            desc: {
                en: "Places a GPS waypoint to guide the player. The chain pauses until the player arrives.",
                fr: "Place un waypoint GPS pour guider le joueur. La chaîne se met en pause jusqu'à l'arrivée du joueur.",
            },
            when: {
                en: "Guided tutorials, quest navigation, directing players to objectives, discovery mechanics.",
                fr: "Tutoriels guidés, navigation de quête, direction vers des objectifs, mécaniques de découverte.",
            },
            options: [
                {
                    field: "targetItemId",
                    label: { en: "Target entity", fr: "Entité cible" },
                    detail: { en: "Which entity to guide the player to.", fr: "Vers quelle entité guider le joueur." },
                },
            ],
            effect: {
                en: "A waypoint marker guides the player. Cross-zone targets are resolved through portals automatically.",
                fr: "Un marqueur guide le joueur. Les cibles inter-zones sont résolues via les portails automatiquement.",
            },
            related: ["Teleport", "Notify", "Dialogue"],
        },
    },
    {
        type: "Compare",
        icon: "/icons/compare.svg",
        category: "narrative",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Compare two values", fr: "Comparer deux valeurs" },
            desc: {
                en: "Compares two values and goes through Yes if the comparison holds, No otherwise.",
                fr: "Compare deux valeurs : passe par Vrai si la comparaison est vérifiée, Faux sinon.",
            },
            when: {
                en: "Pick a path based on a math comparison: vitality > 50, gold == 0, level >= 10.",
                fr: "Choisir un chemin selon une comparaison : vitalité > 50, or == 0, niveau >= 10.",
            },
            options: [
                {
                    field: "operator",
                    label: { en: "Operator", fr: "Opérateur" },
                    detail: {
                        en: "Greater than, less than, equal, not equal, etc.",
                        fr: "Plus grand, plus petit, égal, différent, etc.",
                    },
                },
                {
                    field: "a",
                    label: { en: "First value", fr: "Première valeur" },
                    detail: {
                        en: "Wire a value (e.g. vitality from a Get block).",
                        fr: "Branche une valeur (par exemple la vitalité depuis un bloc Get).",
                    },
                },
                {
                    field: "b",
                    label: { en: "Second value", fr: "Deuxième valeur" },
                    detail: {
                        en: "Wire a value or set a fixed number.",
                        fr: "Branche une valeur ou mets un nombre fixe.",
                    },
                },
            ],
            effect: {
                en: "Goes through Yes or No depending on the comparison result.",
                fr: "Passe par Vrai ou Faux selon le résultat de la comparaison.",
            },
            related: ["Branch", "WaitUntil"],
        },
    },
    {
        type: "Branch",
        icon: "/icons/branch.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Condition (if / else)", fr: "Condition (si / sinon)" },
            desc: {
                en: "Checks something (e.g. does the player have 100 coins?) and picks a path based on the answer. Combine several conditions with AND / OR.",
                fr: "Vérifie quelque chose (ex : le joueur a-t-il 100 pièces ?) et choisit un chemin selon la réponse. Combine plusieurs conditions avec ET / OU.",
            },
            when: {
                en: "Shop purchases (check gold), locked doors (check key + level), level-gated content, branching dialogues.",
                fr: "Achats en boutique (vérifier l'or), portes verrouillées (vérifier clé + niveau), contenu bloqué par niveau, dialogues à embranchements.",
            },
            options: [
                {
                    field: "logicalOperator",
                    label: { en: "Logical operator", fr: "Opérateur logique" },
                    detail: { en: "AND (all must be true) or OR (at least one must be true).", fr: "ET (toutes doivent être vraies) ou OU (au moins une doit être vraie)." },
                },
                {
                    field: "conditions",
                    label: { en: "Conditions", fr: "Conditions" },
                    detail: { en: "Checks on items, stats, currencies.", fr: "Vérifications sur items, stats, devises." },
                },
            ],
            effect: {
                en: "Conditions are combined via the chosen operator, then the matching branch is executed.",
                fr: "Les conditions sont combinées via l'opérateur choisi, puis la branche correspondante est exécutée.",
            },
            related: ["WaitUntil", "ModifyCurrency", "Notify"],
        },
    },
    {
        type: "WaitUntil",
        icon: "/icons/wait.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Wait for condition", fr: "Attendre une condition" },
            desc: {
                en: "Pauses the chain until a condition becomes true (e.g. the player reaches 10 HP). The engine checks every 2 seconds and resumes automatically.",
                fr: "Met la chaîne en pause jusqu'à ce qu'une condition devienne vraie (ex : le joueur atteint 10 de vie). Le moteur vérifie toutes les 2 secondes et reprend automatiquement.",
            },
            when: {
                en: "Wait for the player to collect enough items, reach a stat threshold, earn currency — or any combination.",
                fr: "Attendre que le joueur collecte assez d'items, atteigne un seuil de stat, gagne de la devise — ou toute combinaison.",
            },
            options: [
                {
                    field: "logicalOperator",
                    label: { en: "Logical operator", fr: "Opérateur logique" },
                    detail: { en: "AND / OR — combine multiple conditions.", fr: "ET / OU — combine plusieurs conditions." },
                },
                {
                    field: "conditions",
                    label: { en: "Conditions", fr: "Conditions" },
                    detail: { en: "Same options as Condition (if / else).", fr: "Mêmes options que Condition (si / sinon)." },
                },
            ],
            effect: {
                en: "The chain pauses. Every 2 seconds the conditions are re-evaluated. When satisfied, the next action fires.",
                fr: "La chaîne se met en pause. Toutes les 2 secondes les conditions sont réévaluées. Quand c'est bon, l'action suivante se déclenche.",
            },
            related: ["Branch", "SetGoal", "Notify"],
        },
    },
    {
        type: "Teleport",
        icon: "/icons/teleport.svg",
        category: "exploration",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Teleport", fr: "Téléporter" },
            desc: {
                en: "Moves the player to another location. GPS waypoints are recalculated automatically after the move.",
                fr: "Déplace le joueur vers un autre endroit. Les points GPS sont recalculés automatiquement après le déplacement.",
            },
            when: {
                en: "Portals, doors, dungeon entrances, fast travel points, story transitions.",
                fr: "Portails, portes, entrées de donjon, points de voyage rapide, transitions narratives.",
            },
            options: [
                {
                    field: "destination",
                    label: { en: "Destination", fr: "Destination" },
                    detail: { en: "Which zone or spawn point to teleport to. Set via Studio Linker.", fr: "Vers quelle zone ou point d'apparition téléporter. Défini via le Linker Studio." },
                },
            ],
            effect: {
                en: "The player is teleported with a fade transition. Active GPS waypoints are recalculated for the new zone.",
                fr: "Le joueur est téléporté avec une transition en fondu. Les points GPS actifs sont recalculés pour la nouvelle zone.",
            },
            related: ["SetGoal", "Interact"],
        },
	    },
	    {
	        type: "CreateTeam",
	        icon: "/icons/teleport.svg",
	        category: "exploration",
	        validContexts: PLAYER_CONTEXTS,
	        requiresPlayer: true,
	        requiresEntity: false,
	        requiresSourceItem: false,
	        isBlocking: false,
	        skippedWhenMissingContext: true,
	        content: {
	            name: { en: "Create team", fr: "Créer une équipe" },
	            desc: {
	                en: "Creates a runtime team led by the player.",
	                fr: "Crée une équipe runtime dont le joueur devient le leader.",
	            },
	            when: {
	                en: "Village team buttons, co-op party setup, match preparation.",
	                fr: "Boutons d'équipe au village, préparation co-op, préparation de match.",
	            },
	            options: [
	                {
	                    field: "maxSize",
	                    label: { en: "Maximum size", fr: "Taille maximum" },
	                    detail: {
	                        en: "Largest number of players allowed in this team.",
	                        fr: "Nombre maximum de joueurs autorisés dans cette équipe.",
	                    },
	                },
	            ],
	            effect: {
	                en: "The player enters a new runtime team. If they already belong to a team, the Failed path is used.",
	                fr: "Le joueur entre dans une nouvelle équipe runtime. S'il appartient déjà à une équipe, le chemin Failed est utilisé.",
	            },
	            related: ["LeaveTeam", "Teleport"],
	        },
	    },
	    {
	        type: "LeaveTeam",
	        icon: "/icons/teleport.svg",
	        category: "exploration",
	        validContexts: PLAYER_CONTEXTS,
	        requiresPlayer: true,
	        requiresEntity: false,
	        requiresSourceItem: false,
	        isBlocking: false,
	        skippedWhenMissingContext: true,
	        content: {
	            name: { en: "Leave team", fr: "Quitter l'équipe" },
	            desc: {
	                en: "Removes the player from their current runtime team.",
	                fr: "Retire le joueur de son équipe runtime courante.",
	            },
	            when: {
	                en: "Village team management, queue cancellation, returning to solo play.",
	                fr: "Gestion d'équipe au village, annulation de file, retour en solo.",
	            },
	            options: [],
	            effect: {
	                en: "If the leader leaves, leadership moves to the oldest remaining member. If the team becomes empty, it is destroyed.",
	                fr: "Si le leader quitte, le leadership passe au plus ancien membre restant. Si l'équipe devient vide, elle est détruite.",
	            },
	            related: ["CreateTeam", "Teleport"],
	        },
	    },
	    {
	        type: "InvitePlayerToTeam",
	        icon: "/icons/teleport.svg",
	        category: "exploration",
	        validContexts: PLAYER_CONTEXTS,
	        requiresPlayer: true,
	        requiresEntity: false,
	        requiresSourceItem: false,
	        isBlocking: false,
	        skippedWhenMissingContext: true,
	        content: {
	            name: { en: "Invite player to team", fr: "Inviter dans l'équipe" },
	            desc: {
	                en: "Sends a runtime team invitation to a player selected by the UI.",
	                fr: "Envoie une invitation d'équipe runtime au joueur sélectionné par l'UI.",
	            },
	            when: {
	                en: "Village player list, co-op party setup, lobby preparation.",
	                fr: "Liste des joueurs au village, préparation co-op, préparation de lobby.",
	            },
	            options: [
	                {
	                    field: "targetUserId",
	                    label: { en: "Target user", fr: "Joueur cible" },
	                    detail: {
	                        en: "Usually filled from a Server.Players UI row.",
	                        fr: "Généralement rempli depuis une ligne UI Server.Players.",
	                    },
	                },
	                {
	                    field: "maxSize",
	                    label: { en: "Maximum size", fr: "Taille maximum" },
	                    detail: {
	                        en: "Team size created if the inviter is not already in a team.",
	                        fr: "Taille de l'équipe créée si l'inviteur n'a pas encore d'équipe.",
	                    },
	                },
	            ],
	            effect: {
	                en: "Only the team leader can invite. A solo team can be upgraded to the requested size.",
	                fr: "Seul le leader peut inviter. Une équipe solo peut être agrandie à la taille demandée.",
	            },
	            related: ["AcceptTeamInvite", "DeclineTeamInvite", "LeaveTeam"],
	        },
	    },
	    {
	        type: "AcceptTeamInvite",
	        icon: "/icons/teleport.svg",
	        category: "exploration",
	        validContexts: PLAYER_CONTEXTS,
	        requiresPlayer: true,
	        requiresEntity: false,
	        requiresSourceItem: false,
	        isBlocking: false,
	        skippedWhenMissingContext: true,
	        content: {
	            name: { en: "Accept team invitation", fr: "Accepter l'invitation" },
	            desc: {
	                en: "Accepts a pending team invitation selected by the UI.",
	                fr: "Accepte une invitation d'équipe sélectionnée par l'UI.",
	            },
	            when: {
	                en: "Team invitation list, village lobby.",
	                fr: "Liste d'invitations d'équipe, lobby du village.",
	            },
	            options: [
	                {
	                    field: "inviteId",
	                    label: { en: "Invitation", fr: "Invitation" },
	                    detail: {
	                        en: "Usually filled from a Player.TeamInvites UI row.",
	                        fr: "Généralement rempli depuis une ligne UI Player.TeamInvites.",
	                    },
	                },
	            ],
	            effect: {
	                en: "The player leaves their solo team if needed, then joins the inviter's team.",
	                fr: "Le joueur quitte son équipe solo si besoin, puis rejoint l'équipe de l'inviteur.",
	            },
	            related: ["InvitePlayerToTeam", "DeclineTeamInvite", "LeaveTeam"],
	        },
	    },
	    {
	        type: "DeclineTeamInvite",
	        icon: "/icons/teleport.svg",
	        category: "exploration",
	        validContexts: PLAYER_CONTEXTS,
	        requiresPlayer: true,
	        requiresEntity: false,
	        requiresSourceItem: false,
	        isBlocking: false,
	        skippedWhenMissingContext: true,
	        content: {
	            name: { en: "Decline team invitation", fr: "Refuser l'invitation" },
	            desc: {
	                en: "Declines a pending team invitation selected by the UI.",
	                fr: "Refuse une invitation d'équipe sélectionnée par l'UI.",
	            },
	            when: {
	                en: "Team invitation list, village lobby.",
	                fr: "Liste d'invitations d'équipe, lobby du village.",
	            },
	            options: [
	                {
	                    field: "inviteId",
	                    label: { en: "Invitation", fr: "Invitation" },
	                    detail: {
	                        en: "Usually filled from a Player.TeamInvites UI row.",
	                        fr: "Généralement rempli depuis une ligne UI Player.TeamInvites.",
	                    },
	                },
	            ],
	            effect: {
	                en: "The invitation is removed without changing team membership.",
	                fr: "L'invitation est supprimée sans modifier l'équipe.",
	            },
	            related: ["InvitePlayerToTeam", "AcceptTeamInvite"],
	        },
	    },
    {
        type: "SetAmbiance",
        icon: "/icons/ambiance.svg",
        category: "exploration",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Change ambiance", fr: "Changer l'ambiance" },
            desc: {
                en: "Light transition (day, night, dawn…). Set the mood for any moment with a smooth transition.",
                fr: "Transition de lumière (jour, nuit, aube…). Définis l'ambiance de chaque moment avec une transition fluide.",
            },
            when: {
                en: "Entering a cave, day/night transitions, boss fight atmosphere, cutscene mood, seasonal events.",
                fr: "Entrée dans une grotte, transitions jour/nuit, ambiance de boss fight, atmosphère de cinématique, événements saisonniers.",
            },
            options: [
                {
                    field: "preset",
                    label: { en: "Preset", fr: "Préréglage" },
                    detail: { en: "Dawn, Morning, Noon, Afternoon, Dusk, Night or Midnight.", fr: "Aube, Matin, Midi, Après-midi, Crépuscule, Nuit ou Minuit." },
                },
            ],
            effect: {
                en: "Lighting transitions to the target preset. Sky, fog and ambient light all update together.",
                fr: "L'éclairage passe au preset cible. Ciel, brouillard et lumière ambiante changent ensemble.",
            },
            related: ["Teleport", "Dialogue"],
        },
    },
    {
        type: "SetDailyQuest",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Daily quest", fr: "Quête quotidienne" },
            desc: {
                en: "Assigns a daily quest to the player.",
                fr: "Attribue une quête quotidienne au joueur.",
            },
            when: {
                en: "Login bonuses, daily objectives, recurring challenges.",
                fr: "Bonus de connexion, objectifs quotidiens, défis récurrents.",
            },
            options: [],
            effect: {
                en: "A daily quest is assigned to the player and tracked on the HUD.",
                fr: "Une quête quotidienne est attribuée au joueur et suivie sur l'écran.",
            },
            related: ["SetJournalQuest", "GiveQuest"],
        },
    },
    {
        type: "SetJournalQuest",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Journal quest", fr: "Quête journal" },
            desc: {
                en: "Assigns a journal quest to the player.",
                fr: "Attribue une quête de journal au joueur.",
            },
            when: {
                en: "Storyline milestones, long-term objectives tracked in the journal.",
                fr: "Jalons narratifs, objectifs à long terme suivis dans le journal.",
            },
            options: [],
            effect: {
                en: "A journal quest is assigned to the player and tracked in the journal.",
                fr: "Une quête journal est attribuée au joueur et suivie dans le journal.",
            },
            related: ["SetDailyQuest", "GiveQuest"],
        },
    },
    {
        type: "GiveQuest",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Give a quest", fr: "Donner une quête" },
            desc: {
                en: "The NPC gives a quest to the player, picked at random from the list. Takes a different path depending on what happens.",
                fr: "Le PNJ donne une quête au joueur, tirée au hasard dans la liste. Prend un chemin différent selon ce qui se passe.",
            },
            when: {
                en: "Guild NPCs handing out random missions, daily quest givers, storyline NPCs with several possible quests.",
                fr: "PNJ de guilde qui distribuent des missions au hasard, donneurs de quêtes quotidiennes, PNJ d'histoire avec plusieurs quêtes possibles.",
            },
            options: [
                {
                    field: "questIds",
                    label: { en: "Quest list", fr: "Liste de quêtes" },
                    detail: { en: "The quests the NPC can give. One is picked at random from those the player doesn't already have.", fr: "Les quêtes que le PNJ peut donner. Une est tirée au hasard parmi celles que le joueur n'a pas déjà." },
                },
            ],
            effect: {
                en: "Next → the NPC just gave the quest. In progress → the player already has it and it is not done yet. Completed → the player finished it and is waiting for reward. Failed → the quest failed. Impossible → nothing to give.",
                fr: "Suivant → le PNJ vient de donner la quête. En cours → le joueur l'a déjà et elle n'est pas terminée. Terminée → le joueur l'a finie et attend la récompense. Ratée → la quête a échoué. Impossible → rien à donner.",
            },
            related: ["CheckQuestStatus", "ClaimQuest", "CheckoutQuest", "ClearQuestState"],
        },
    },
    {
        type: "CheckQuestStatus",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Check the quest", fr: "Regarder où en est la quête" },
            desc: {
                en: "Looks at whether the player already has the NPC's quest, and picks a different path depending on the state. Does not change anything.",
                fr: "Regarde si le joueur a déjà la quête du PNJ, et choisit un chemin différent selon l'état. Ne modifie rien.",
            },
            when: {
                en: "Use before Give a quest to avoid giving the same quest twice. Use after to branch the dialogue on whether the quest is done, in progress, or failed.",
                fr: "À utiliser avant Donner une quête pour éviter de redonner la même quête. À utiliser après pour aiguiller le dialogue selon que la quête est terminée, en cours, ou ratée.",
            },
            options: [
                {
                    field: "questIds",
                    label: { en: "Quest list", fr: "Liste de quêtes" },
                    detail: { en: "The quests to check. Picks the first one that's bound to this NPC for the player.", fr: "Les quêtes à vérifier. Prend la première qui est liée à ce PNJ pour ce joueur." },
                },
            ],
            effect: {
                en: "In progress → quest given, not yet done. Completed → quest done, ready for reward. Failed → quest failed (time ran out or wrong delivery). Not started → player doesn't have the quest yet.",
                fr: "En cours → quête donnée, pas encore terminée. Terminée → quête réussie, prête à être récompensée. Ratée → quête échouée (temps écoulé ou mauvaise livraison). Pas encore donnée → le joueur n'a pas la quête.",
            },
            related: ["GiveQuest", "ClaimQuest", "CheckoutQuest", "ClearQuestState"],
        },
    },
    {
        type: "ClaimQuest",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Give the reward", fr: "Donner la récompense" },
            desc: {
                en: "The NPC gives the reward to the player. Only works if the player has finished the quest.",
                fr: "Le PNJ donne la récompense au joueur. Ne marche que si le joueur a terminé la quête.",
            },
            when: {
                en: "The player comes back to the NPC after finishing the quest, to collect the reward.",
                fr: "Le joueur revient voir le PNJ après avoir terminé la quête, pour récupérer la récompense.",
            },
            options: [],
            effect: {
                en: "Rewards are given to the player, and the NPC is free to give a new quest.",
                fr: "Les récompenses sont données au joueur, et le PNJ peut donner une nouvelle quête.",
            },
            related: ["GiveQuest", "CheckQuestStatus", "CheckoutQuest"],
        },
    },
    {
        type: "CheckoutQuest",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Deliver the quest", fr: "Livrer la quête" },
            desc: {
                en: "The player has to hand over the right items to the NPC, in the right order. If they get it wrong, the quest fails.",
                fr: "Le joueur doit donner les bons objets au PNJ, dans le bon ordre. S'il se trompe, la quête est ratée.",
            },
            when: {
                en: "Delivery quests (bring 2 mushrooms + 1 flower + 2 turtles) where getting the order wrong means failure.",
                fr: "Quêtes de livraison (apporter 2 champignons + 1 fleur + 2 tortues) où se tromper d'ordre signifie un échec.",
            },
            options: [],
            effect: {
                en: "Right delivery → rewards given, quest finished. Wrong delivery → items lost, quest marked as failed.",
                fr: "Bonne livraison → récompenses données, quête terminée. Mauvaise livraison → objets perdus, quête marquée comme ratée.",
            },
            related: ["GiveQuest", "CheckQuestStatus", "ClaimQuest", "ClearQuestState"],
        },
    },
    {
        type: "ClearQuestState",
        icon: "/icons/goal.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Forget the quest", fr: "Oublier la quête" },
            desc: {
                en: "The NPC forgets the quest they had given. Used so the player can try again after failing.",
                fr: "Le PNJ oublie la quête qu'il avait donnée. Sert à ce que le joueur puisse retenter après un échec.",
            },
            when: {
                en: "After the 'Failed' branch of Check the quest: accept the failure and let the NPC give a new quest.",
                fr: "Après la branche 'Ratée' de Regarder où en est la quête : accepte l'échec et laisse le PNJ donner une nouvelle quête.",
            },
            options: [],
            effect: {
                en: "The NPC can give a new quest the next time the player comes to talk. The failed quest is erased.",
                fr: "Le PNJ peut donner une nouvelle quête la prochaine fois que le joueur vient lui parler. La quête ratée est effacée.",
            },
            related: ["GiveQuest", "CheckQuestStatus", "CheckoutQuest"],
        },
    },
    {
        type: "Choice",
        icon: "/icons/dialogue.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Multiple choice", fr: "Choix multiple" },
            desc: {
                en: "Shows a prompt to the player with a list of response buttons. Each response opens its own branch in the graph.",
                fr: "Affiche une question au joueur avec une liste de boutons de réponse. Chaque réponse ouvre sa propre branche dans le graph.",
            },
            when: {
                en: "NPC dialogue forks, quest acceptance prompts, shop menus, dilemmas, player-driven narrative.",
                fr: "Embranchements de dialogue PNJ, validation de quête, menus de boutique, dilemmes, narration pilotée par le joueur.",
            },
            options: [
                {
                    field: "prompt",
                    label: { en: "Prompt", fr: "Phrase" },
                    detail: { en: "The question shown above the response buttons.", fr: "La question affichée au-dessus des boutons de réponse." },
                },
                {
                    field: "cases",
                    label: { en: "Responses", fr: "Réponses" },
                    detail: { en: "List of response labels. Each one becomes an output handle.", fr: "Liste des libellés de réponse. Chaque libellé devient un handle de sortie." },
                },
            ],
            effect: {
                en: "The chain pauses, a choice window opens on the player's client, and continues through the handle matching the player's pick (or Default on cancel).",
                fr: "La chaîne se met en pause, une fenêtre de choix s'ouvre chez le joueur, puis reprend via le handle correspondant à la réponse choisie (ou Default si annulation).",
            },
            related: ["Dialogue", "Branch", "Switch"],
        },
    },
    {
        type: "PlaySound",
        icon: "/icons/ambiance.svg",
        category: "exploration",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Play a sound", fr: "Jouer un son" },
            desc: {
                en: "Plays a sound on the player's client. The sound lives in Workspace and is referenced by a dot path.",
                fr: "Joue un son sur le client du joueur. Le son est dans Workspace et référencé par un chemin pointé.",
            },
            when: {
                en: "Pickup chimes, ambient stings, UI feedback, boss roar, victory jingles.",
                fr: "Sons de ramassage, stingers d'ambiance, feedback UI, rugissement de boss, jingle de victoire.",
            },
            options: [
                {
                    field: "soundPath",
                    label: { en: "Sound", fr: "Son" },
                    detail: { en: "Workspace dot path, e.g. Assets.Sounds.Boom.", fr: "Chemin pointé Workspace, ex : Assets.Sounds.Boom." },
                },
            ],
            effect: {
                en: "Fires once on the player's client via the SoundController.",
                fr: "Joue une fois sur le client du joueur via le SoundController.",
            },
            related: ["ShowTitle", "Notify"],
        },
    },
    {
        type: "PlayAnimation",
        icon: "/icons/behavior.svg",
        category: "combat",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Play an animation", fr: "Jouer une animation" },
            desc: {
                en: "Plays an animation on a resolved player, item or world model.",
                fr: "Joue une animation sur un joueur, item ou modèle du monde résolu.",
            },
            when: {
                en: "Sawing wood (player), boss intro pose (item character), NPC wave, item dance.",
                fr: "Scier du bois (joueur), pose d'intro de boss (personnage de l'objet), PNJ qui salue, danse d'objet.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Who plays it?", fr: "Qui la joue ?" },
                    detail: {
                        en: "player, Entity, self, selected_target or $event.target.",
                        fr: "player, Entity, self, selected_target ou $event.target.",
                    },
                },
                {
                    field: "animationPath",
                    label: { en: "Animation", fr: "Animation" },
                    detail: {
                        en: "Pick the animation from Roblox Studio with the Kitsulab plugin.",
                        fr: "Choisis l'animation depuis Roblox Studio avec le plugin Kitsulab.",
                    },
                },
            ],
            effect: {
                en: "Plays the animation. Three outputs: OnStart (right away), OnEnd (when the animation finishes), OnEvent (each time the animation reaches a marker). Each branch runs in parallel — nothing blocks.",
                fr: "Joue l'animation. Trois sorties : OnStart (tout de suite), OnEnd (à la fin de l'animation), OnEvent (à chaque marqueur dans l'animation). Chaque branche tourne en parallèle — rien ne bloque.",
            },
            related: ["ChangeBehavior", "PlaySound"],
        },
    },
    {
        type: "ShowTitle",
        icon: "/icons/notify.svg",
        category: "narrative",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Show a title", fr: "Afficher un titre" },
            desc: {
                en: "Displays a large styled title on the player's screen, with optional sound. Reuses the engine's title display system.",
                fr: "Affiche un grand titre stylisé à l'écran du joueur, avec son optionnel. Réutilise le système d'affichage de titre du moteur.",
            },
            when: {
                en: "Boss defeat banner, achievement reveal, chapter title card, level-up flourish.",
                fr: "Bandeau de défaite de boss, annonce de succès, carte titre de chapitre, fanfare de level-up.",
            },
            options: [
                {
                    field: "text",
                    label: { en: "Title text", fr: "Texte du titre" },
                    detail: { en: "The title text to display.", fr: "Le texte du titre à afficher." },
                },
            ],
            effect: {
                en: "Renders the title with the gold gradient style for ~1.5s on the player's screen.",
                fr: "Affiche le titre avec le style gradient doré pendant ~1.5s à l'écran du joueur.",
            },
            related: ["PlaySound", "Notify"],
        },
    },
    {
        type: "OnProximity",
        icon: "/icons/proximity.svg",
        category: "event",
        validContexts: ENTITY_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: true,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Detect proximity", fr: "Détecter une proximité" },
            desc: {
                en: "Watches for players or world items entering/leaving a radius around the entity. OnEnter and OnExit expose the detected target as $event.target.",
                fr: "Surveille les joueurs ou items du monde qui entrent/sortent d'un rayon autour de l'entité. OnEnter et OnExit exposent la cible via $event.target.",
            },
            when: {
                en: "Aggressive mob that wakes when you approach, NPC that greets you within range, trap zone, ambient dialogue trigger.",
                fr: "Mob agressif qui se réveille à votre approche, PNJ qui salue dans son rayon, zone de piège, dialogue d'ambiance.",
            },
            options: [
                {
                    field: "radius",
                    label: { en: "Radius (studs)", fr: "Rayon (studs)" },
                    detail: {
                        en: "Distance in studs. Use 0 for direct contact with the entity's root part.",
                        fr: "Distance en studs. Utilise 0 pour un contact direct avec la pièce racine de l'entité.",
                    },
                },
                {
                    field: "targetKind",
                    label: { en: "Target kind", fr: "Type de cible" },
                    detail: { en: "Players, Items, or both.", fr: "Joueurs, items, ou les deux." },
                },
                {
                    field: "relationship",
                    label: { en: "Relationship", fr: "Relation" },
                    detail: { en: "Any, Owner, or NonOwner relative to this entity.", fr: "Tous, propriétaire, ou non-propriétaire par rapport à cette entité." },
                },
                {
                    field: "itemIds",
                    label: { en: "Item filters", fr: "Filtres d'items" },
                    detail: { en: "Optional item types to detect when target kind includes Items.", fr: "Types d'items optionnels à détecter quand le type inclut Items." },
                },
            ],
            effect: {
                en: "Registers a proximity watcher on the entity. OnEnter/OnExit fire once per boundary crossing and carry the target payload.",
                fr: "Enregistre un watcher de proximité sur l'entité. OnEnter/OnExit se déclenchent une fois par franchissement et transportent la cible.",
            },
            related: ["ChangeBehavior", "Dialogue", "Branch"],
        },
    },
    {
        type: "DisplayInterface",
        icon: "/icons/notify.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Display interface", fr: "Afficher une interface" },
            desc: {
                en: "Shows a UI tree on the player's screen by name.",
                fr: "Affiche une interface (UI Tree) sur l'écran du joueur par son nom.",
            },
            when: {
                en: "Show the HUD on player join, open the inventory window on click, display a shop modal.",
                fr: "Afficher le HUD à l'arrivée du joueur, ouvrir l'inventaire au clic, afficher une modale boutique.",
            },
            options: [
                {
                    field: "interfaceName",
                    label: { en: "Interface name", fr: "Nom de l'interface" },
                    detail: {
                        en: "The name of the UI Tree to display (as named in the CMS).",
                        fr: "Le nom de l'interface (UI Tree) à afficher, tel que défini dans le CMS.",
                    },
                },
            ],
            effect: {
                en: "Builds the UI tree on the player's PlayerGui and shows it on top of any other open UI.",
                fr: "Construit l'interface dans le PlayerGui du joueur et l'affiche par-dessus les autres UI.",
            },
            related: ["HideInterface"],
        },
    },
    {
        type: "HideInterface",
        icon: "/icons/notify.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Hide interface", fr: "Masquer une interface" },
            desc: {
                en: "Hides a UI tree currently shown to the player by name.",
                fr: "Masque une interface (UI Tree) actuellement affichée au joueur par son nom.",
            },
            when: {
                en: "Close the inventory window, hide the shop modal, dismiss a notification panel.",
                fr: "Fermer l'inventaire, masquer la modale boutique, dissimuler un panneau de notification.",
            },
            options: [
                {
                    field: "interfaceName",
                    label: { en: "Interface name", fr: "Nom de l'interface" },
                    detail: {
                        en: "The name of the UI Tree to hide.",
                        fr: "Le nom de l'interface (UI Tree) à masquer.",
                    },
                },
            ],
            effect: {
                en: "The UI tree disappears from the player's screen. Other UI trees remain visible.",
                fr: "L'interface disparaît de l'écran du joueur. Les autres interfaces restent affichées.",
            },
            related: ["DisplayInterface"],
        },
    },
    {
        type: "SetWorldProperty",
        icon: "/icons/ui-property.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Set world property", fr: "Modifier une propriété du monde" },
            desc: {
                en: "Mutates a Roblox world property on a part or model, such as size, visibility, collision or color.",
                fr: "Modifie une propriété Roblox d'une pièce ou d'un modèle, comme la taille, la visibilité, la collision ou la couleur.",
            },
            when: {
                en: "Laser doors, traps, visible shields, temporary blockers, color feedback.",
                fr: "Portes laser, pièges, boucliers visibles, bloqueurs temporaires, feedback couleur.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Target", fr: "Cible" },
                    detail: { en: "self, $event.target, a child name, or a Workspace path.", fr: "self, $event.target, un enfant, ou un chemin Workspace." },
                },
                {
                    field: "propertyKey",
                    label: { en: "Property", fr: "Propriété" },
                    detail: { en: "Width, Height, Depth, Scale, Position, Rotation, Visible, Transparency, CanCollide, Anchored or Color.", fr: "Width, Height, Depth, Scale, Position, Rotation, Visible, Transparency, CanCollide, Anchored ou Color." },
                },
                {
                    field: "value",
                    label: { en: "Value", fr: "Valeur" },
                    detail: { en: "Number, boolean, color or vector depending on the property.", fr: "Nombre, booléen, couleur ou vecteur selon la propriété." },
                },
            ],
            effect: {
                en: "The world object updates immediately on the server.",
                fr: "L'objet du monde est modifié immédiatement côté serveur.",
            },
            related: ["Interact", "OnProximity", "SetTimer"],
        },
    },
    {
        type: "TweenWorldProperty",
        icon: "/icons/ui-property.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Tween world property", fr: "Animer une propriété du monde" },
            desc: {
                en: "Smoothly animates a Roblox world property on a part or model, such as position, size, transparency or color.",
                fr: "Anime en douceur une propriété Roblox d'une pièce ou d'un modèle, comme la position, la taille, la transparence ou la couleur.",
            },
            when: {
                en: "Opening doors, fading lasers, moving platforms, warning colors and trap feedback.",
                fr: "Ouverture de portes, fondu de lasers, plateformes mobiles, couleurs d'alerte et feedback de piège.",
            },
            options: [
                {
                    field: "target",
                    label: { en: "Target", fr: "Cible" },
                    detail: { en: "self, $event.target, a child name, or a Workspace path.", fr: "self, $event.target, un enfant, ou un chemin Workspace." },
                },
                {
                    field: "propertyKey",
                    label: { en: "Property", fr: "Propriété" },
                    detail: { en: "Width, Height, Depth, Scale, Position, Rotation, Visible, Transparency, CanCollide, Anchored or Color.", fr: "Width, Height, Depth, Scale, Position, Rotation, Visible, Transparency, CanCollide, Anchored ou Color." },
                },
                {
                    field: "value",
                    label: { en: "Value", fr: "Valeur" },
                    detail: { en: "Number, boolean, color or vector depending on the property.", fr: "Nombre, booléen, couleur ou vecteur selon la propriété." },
                },
                {
                    field: "duration",
                    label: { en: "Duration", fr: "Durée" },
                    detail: { en: "Animation duration in seconds.", fr: "Durée de l'animation en secondes." },
                },
                {
                    field: "delay",
                    label: { en: "Delay", fr: "Délai" },
                    detail: { en: "Wait time before starting, in seconds.", fr: "Temps d'attente avant de démarrer, en secondes." },
                },
                {
                    field: "easing",
                    label: { en: "Easing", fr: "Courbe" },
                    detail: { en: "How the motion accelerates and slows down.", fr: "La façon dont le mouvement accélère et ralentit." },
                },
            ],
            effect: {
                en: "The world object animates on the server, then keeps the final value.",
                fr: "L'objet du monde s'anime côté serveur, puis conserve la valeur finale.",
            },
            related: ["SetWorldProperty", "Interact", "OnProximity"],
        },
    },
    {
        type: "SetUiProperty",
        icon: "/icons/ui-property.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Set UI property", fr: "Modifier une propriété d'interface" },
            desc: {
                en: "Mutates one property of an interface element. Pick the property in the dropdown; the value field adapts to its type.",
                fr: "Modifie une propriété d'un élément d'interface. Choisis la propriété dans la liste ; le champ valeur s'adapte au type.",
            },
            when: {
                en: "Update a score label, recolor a button on hover, show or hide a panel.",
                fr: "Mettre à jour un score, recolorer un bouton au survol, afficher ou masquer un panneau.",
            },
            options: [
                {
                    field: "nodeId",
                    label: { en: "Target", fr: "Cible" },
                    detail: {
                        en: "The element to mutate. Use $self for the element that triggered this graph.",
                        fr: "L'élément à modifier. Utilise $self pour l'élément qui a déclenché ce graphe.",
                    },
                },
                {
                    field: "propertyKey",
                    label: { en: "Property", fr: "Propriété" },
                    detail: {
                        en: "Pick the property to set (Text, Image, BackgroundColor, Visible, ...).",
                        fr: "Choisis la propriété à modifier (Text, Image, BackgroundColor, Visible, ...).",
                    },
                },
                {
                    field: "value",
                    label: { en: "Value", fr: "Valeur" },
                    detail: {
                        en: "The new value. Type matches the picked property. Wire a data edge or type a literal.",
                        fr: "La nouvelle valeur. Le type suit la propriété choisie. Branche un fil ou tape une valeur.",
                    },
                },
            ],
            effect: {
                en: "The property updates immediately on the player's screen.",
                fr: "La propriété se met à jour immédiatement à l'écran du joueur.",
            },
            related: ["DisplayInterface", "HideInterface", "TweenProperty"],
        },
    },
    {
        type: "TweenProperty",
        icon: "/icons/ui-property.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Tween UI property", fr: "Animer une propriété d'interface" },
            desc: {
                en: "Smoothly animates one property of an interface element over time. Use it for HP bar drains, hover transitions, fade in/out.",
                fr: "Anime en douceur une propriété d'un élément d'interface. Sert pour les barres de vie qui descendent, les transitions au survol, les fondus.",
            },
            when: {
                en: "Decrease a stat bar smoothly when HP drops, fade in a notification, scale up a card on hover.",
                fr: "Faire descendre une barre de vie en douceur, faire apparaître une notif en fondu, agrandir une carte au survol.",
            },
            options: [
                {
                    field: "nodeId",
                    label: { en: "Target", fr: "Cible" },
                    detail: {
                        en: "The element to animate. Use $self for the element that triggered this graph.",
                        fr: "L'élément à animer. Utilise $self pour l'élément qui a déclenché ce graphe.",
                    },
                },
                {
                    field: "propertyKey",
                    label: { en: "Property", fr: "Propriété" },
                    detail: {
                        en: "Pick the property to animate. Numeric and color properties work best.",
                        fr: "Choisis la propriété à animer. Les nombres et couleurs marchent le mieux.",
                    },
                },
                {
                    field: "value",
                    label: { en: "Target value", fr: "Valeur cible" },
                    detail: {
                        en: "The value the property should reach at the end of the animation.",
                        fr: "La valeur que la propriété doit atteindre à la fin de l'animation.",
                    },
                },
                {
                    field: "duration",
                    label: { en: "Duration (ms)", fr: "Durée (ms)" },
                    detail: {
                        en: "How long the animation lasts, in milliseconds.",
                        fr: "Combien de temps dure l'animation, en millisecondes.",
                    },
                },
                {
                    field: "delay",
                    label: { en: "Delay (ms)", fr: "Délai (ms)" },
                    detail: {
                        en: "Wait this long before starting the animation.",
                        fr: "Attendre cette durée avant de lancer l'animation.",
                    },
                },
                {
                    field: "easing",
                    label: { en: "Easing", fr: "Courbe" },
                    detail: {
                        en: "How the animation accelerates and decelerates.",
                        fr: "Comment l'animation accélère et ralentit.",
                    },
                },
            ],
            effect: {
                en: "The property animates from its current value to the target over `duration` ms.",
                fr: "La propriété s'anime de sa valeur actuelle vers la cible sur `duration` ms.",
            },
            related: ["SetUiProperty"],
        },
    },
    {
        type: "ForEach",
        icon: "/icons/foreach.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "For each", fr: "Pour chaque" },
            desc: {
                en: "Loops over a list and runs the Body branch once per item.",
                fr: "Boucle sur une liste et exécute la branche Body une fois par élément.",
            },
            when: {
                en: "Drawing N hearts based on vitality, listing items, repeating an action per element.",
                fr: "Dessiner N cœurs selon la vie, lister des items, répéter une action par élément.",
            },
            options: [
                {
                    field: "list",
                    label: { en: "List (data input)", fr: "Liste (entrée data)" },
                    detail: {
                        en: "Wire a list-producing data node (Range, GetItemList).",
                        fr: "Branche un bloc qui produit une liste (Range, GetItemList).",
                    },
                },
            ],
            effect: {
                en: "Clones the first child node once per item (push from parent), then fires Done.",
                fr: "Clone le premier enfant une fois par élément (push depuis le parent), puis tire Done.",
            },
            related: ["SetUiProperty", "Get"],
        },
    },
    {
        type: "ForEachPlayer",
        icon: "/icons/foreach.svg",
        category: "exploration",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "For each player", fr: "Pour chaque joueur" },
            desc: {
                en: "Runs the next branch once for each player in the chosen scope.",
                fr: "Exécute la suite une fois pour chaque joueur du périmètre choisi.",
            },
            when: {
                en: "Island timers, round titles, rewards for all place owners, global announcements.",
                fr: "Timers d'île, titres de round, récompenses pour les propriétaires de places, annonces globales.",
            },
            options: [
                {
                    field: "scope",
                    label: { en: "Players", fr: "Joueurs" },
                    detail: {
                        en: "AllConnected targets the whole server. PlaceOwners targets owners of claimable places linked to this item.",
                        fr: "AllConnected cible tout le serveur. PlaceOwners cible les propriétaires des places liées à cet objet.",
                    },
                },
            ],
            effect: {
                en: "The following actions run with each matched player as the active player.",
                fr: "Les actions suivantes se lancent avec chaque joueur trouvé comme joueur actif.",
            },
            related: ["SetTimer", "ShowTitle", "Notify"],
        },
    },
    {
        type: "AddChild",
        icon: "/icons/add-child.svg",
        category: "interface",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Add child", fr: "Ajouter un enfant" },
            desc: {
                en: "Appends a cloned template node as a child of another UI node.",
                fr: "Ajoute un nœud template cloné comme enfant d'un autre nœud UI.",
            },
            when: {
                en: "Dynamically populating a list or container with cloned UI blocks.",
                fr: "Remplir dynamiquement une liste ou un conteneur avec des blocs UI clonés.",
            },
            options: [
                {
                    field: "parentNodeId",
                    label: { en: "Parent node ID", fr: "ID du nœud parent" },
                    detail: {
                        en: "The node that will receive the new child. Use $self for the current context node.",
                        fr: "Le nœud qui recevra l'enfant. Utilise $self pour le nœud de contexte courant.",
                    },
                },
                {
                    field: "templateName",
                    label: { en: "Template name", fr: "Nom du template" },
                    detail: {
                        en: "Name of the child template defined in the UiTree.",
                        fr: "Nom du template enfant défini dans l'UiTree.",
                    },
                },
                {
                    field: "scope",
                    label: { en: "Scope", fr: "Portée" },
                    detail: {
                        en: "Optional scope key to pass data into the cloned node (defaults to ItemTemplate).",
                        fr: "Clé de portée optionnelle pour passer des données dans le nœud cloné (défaut : ItemTemplate).",
                    },
                },
            ],
            effect: {
                en: "Clears the parent's existing children on first call, then appends a new clone each time.",
                fr: "Efface les enfants existants du parent au premier appel, puis ajoute un clone à chaque fois.",
            },
            related: ["ForEach", "SetUiProperty"],
        },
    },
    {
        type: "TransformRecipeItem",
        icon: "/icons/recipe-transform.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Build the chosen item", fr: "Lancer la fabrication" },
            desc: {
                en: "Builds the item the player picked: takes the ingredients, gives back the result. Goes to Success if it works, Cancelled if the player doesn't have enough.",
                fr: "Fabrique l'objet choisi : prend les ingrédients, donne le résultat. Va sur Réussi si ça marche, sur Annulé si le joueur n'a pas assez.",
            },
            when: {
                en: "On the Craft / Confirm button of your shop window.",
                fr: "Sur le bouton Fabriquer / OK de ta fenêtre Boutique.",
            },
            options: [],
            effect: {
                en: "Removes the recipe ingredients from the inventory, adds the result. The shop window closes.",
                fr: "Retire les ingrédients de l'inventaire, ajoute le résultat. La fenêtre Boutique se ferme.",
            },
            related: ["Transform"],
        },
    },
    {
        type: "SelectChoice",
        icon: "/icons/choice-select.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Pick this answer", fr: "Valider ce choix" },
            desc: {
                en: "When the player clicks one of the answers, this validates it and the dialogue continues.",
                fr: "Quand le joueur clique sur une des réponses, ça valide et l'histoire continue.",
            },
            when: {
                en: "On each answer button of your question window.",
                fr: "Sur chaque bouton de réponse de ta fenêtre Question.",
            },
            options: [],
            effect: {
                en: "The question window closes and the story continues on the picked path.",
                fr: "La fenêtre Question se ferme et l'histoire continue sur le chemin choisi.",
            },
            related: ["Choice"],
        },
    },
    {
        type: "TransformFreeModeSlots",
        icon: "/icons/recipe-transform.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Try the mix", fr: "Tester le mélange" },
            desc: {
                en: "Tries to match the items the player dropped against your recipes. Goes to Success when one matches and the result is given, Cancelled when nothing matches.",
                fr: "Essaie de retrouver une recette qui colle aux ingrédients déposés. Va sur Réussi si ça marche, sur Annulé si aucune recette ne correspond.",
            },
            when: {
                en: "On the Cook / Confirm button of your free mode panel.",
                fr: "Sur le bouton Cuisiner / OK de ton panneau Mode libre.",
            },
            options: [],
            effect: {
                en: "Removes the slot ingredients from the inventory if a recipe matches, then closes the panel.",
                fr: "Retire les ingrédients de l'inventaire si une recette correspond, puis ferme le panneau.",
            },
            related: ["AppendState", "RemoveState"],
        },
    },
    {
        type: "DeliverQuestItems",
        icon: "/icons/recipe-transform.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: true,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Hand in quest items", fr: "Livrer les objets de quête" },
            desc: {
                en: "Hands the items the player put in the slots to the active NPC quest. Goes to Success when the quest accepts the delivery, Cancelled when it refuses or there is no active quest.",
                fr: "Donne les objets déposés dans les emplacements à la quête active du PNJ. Va sur Réussi si la quête accepte la livraison, sur Annulé si elle refuse ou s'il n'y a pas de quête active.",
            },
            when: {
                en: "On the Confirm button of your quest checkout panel.",
                fr: "Sur le bouton Valider de ton panneau de livraison de quête.",
            },
            options: [],
            effect: {
                en: "Removes the delivered items from the inventory, marks the quest claimable, and closes the panel.",
                fr: "Retire les objets livrés de l'inventaire, marque la quête comme à réclamer, et ferme le panneau.",
            },
            related: ["CheckoutQuest", "ClaimQuest", "TransformFreeModeSlots"],
        },
    },
    {
        type: "SelectTargetItem",
        icon: "/icons/choice-select.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Pick this item", fr: "Choisir cet objet" },
            desc: {
                en: "When the player clicks one of the items in the picker, this validates it and continues the chain that asked for the choice.",
                fr: "Quand le joueur clique sur un des objets du sélecteur, ça valide et reprend l'enchaînement qui demandait le choix.",
            },
            when: {
                en: "On each item button in your target selector window.",
                fr: "Sur chaque bouton d'objet de ta fenêtre Sélecteur.",
            },
            options: [],
            effect: {
                en: "The selector window closes and the story continues with the picked item.",
                fr: "La fenêtre Sélecteur se ferme et la suite continue avec l'objet choisi.",
            },
            related: ["SelectChoice"],
        },
    },
    {
        type: "ClaimQuestById",
        icon: "/icons/quest-claim.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Claim quest reward", fr: "Récolter une quête" },
            desc: {
                en: "Claims the reward of a finished quest by its id. Triggered from the quest journal UI when the player clicks 'Claim'.",
                fr: "Réclame la récompense d'une quête terminée par son id. Déclenchée depuis le carnet de quêtes quand le joueur clique 'Récolter'.",
            },
            when: {
                en: "On the 'Claim' button of each quest row in the quest journal.",
                fr: "Sur le bouton 'Récolter' de chaque ligne du carnet de quêtes.",
            },
            options: [],
            effect: {
                en: "The quest is removed from active quests, its rewards are granted, and the journal refreshes.",
                fr: "La quête est retirée des quêtes actives, ses récompenses sont accordées, et le carnet se met à jour.",
            },
            related: ["ClaimQuest", "SetQuestGoal"],
        },
    },
    {
        type: "SetQuestGoal",
        icon: "/icons/quest-goal.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Set GPS to quest target", fr: "GPS vers la quête" },
            desc: {
                en: "Sets the player's GPS goal to the first incomplete target of a quest. Triggered from the quest journal 'GPS' button.",
                fr: "Positionne le GPS du joueur sur la prochaine cible d'une quête. Déclenchée depuis le bouton 'GPS' du carnet de quêtes.",
            },
            when: {
                en: "On the 'GPS' button of each quest row in the quest journal.",
                fr: "Sur le bouton 'GPS' de chaque ligne du carnet de quêtes.",
            },
            options: [],
            effect: {
                en: "An arrow appears in the world pointing to the quest's next item to collect/kill/craft.",
                fr: "Une flèche apparaît dans le monde et pointe vers le prochain objectif de la quête.",
            },
            related: ["ClaimQuestById"],
        },
    },
    {
        type: "UseInventoryItem",
        icon: "/icons/inventory.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Use inventory item", fr: "Utiliser un objet" },
            desc: {
                en: "Uses the inventory item identified by the bound 'instanceId' input. Routes to Consume or ToggleEquip depending on the item category.",
                fr: "Utilise l'objet de l'inventaire identifié par l'entrée 'instanceId'. Route vers Consommer ou Équiper selon la catégorie.",
            },
            when: {
                en: "On the 'Use' / 'Equip' button of the inventory detail panel.",
                fr: "Sur le bouton 'Utiliser' / 'Équiper' du panneau de détail de l'inventaire.",
            },
            options: [],
            effect: {
                en: "Collectibles and Consumables are consumed; Main/Body/Companion items are toggled equipped.",
                fr: "Les Collectibles et Consommables sont consommés ; les objets Arme/Armure/Compagnon sont équipés/déséquipés.",
            },
            related: ["ConsumeItem", "ToggleEquip"],
        },
    },
    {
        type: "OpenSession",
        icon: "/icons/open-window.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Open UI session", fr: "Ouvrir une session UI" },
            desc: {
                en: "Asks the server to open the named UI session (Inventory, Quest, etc.). The server's session registry dispatches to the corresponding service.",
                fr: "Demande au serveur d'ouvrir la session UI nommée (Inventaire, Quête, etc.). Le registre côté serveur dispatche vers le service correspondant.",
            },
            when: {
                en: "From HUD buttons or any action graph triggering a popup.",
                fr: "Depuis les boutons du HUD ou tout graph qui ouvre un popup.",
            },
            options: [],
            effect: {
                en: "The corresponding UiTree opens with its session payload populated by the server.",
                fr: "L'UiTree correspondant s'ouvre avec son payload de session calculé côté serveur.",
            },
            related: ["DisplayInterface", "HideInterface"],
        },
    },
    {
        type: "Attack",
        icon: "/icons/attack.svg",
        category: "combat",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Player attack", fr: "Attaque joueur" },
            desc: {
                en: "Enqueues a player attack input (combo queue). Used by HUD attack button or input bindings.",
                fr: "Ajoute une entrée d'attaque dans la file combo. Utilisé par le bouton attaque du HUD ou les bindings d'inputs.",
            },
            when: {
                en: "From the HUD attack button, or any UI graph that fires player combat input.",
                fr: "Depuis le bouton d'attaque du HUD, ou tout graph UI qui déclenche une attaque joueur.",
            },
            options: [],
            effect: {
                en: "The combo queue processes the next swing using the player's equipped weapon attacks.",
                fr: "La file combo traite le swing suivant avec les attaques de l'arme équipée du joueur.",
            },
            related: [],
        },
    },
    {
        type: "CollectTimer",
        icon: "/icons/timer-collect.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Collect timer", fr: "Collecter timer" },
            desc: {
                en: "Consumes a ready item (status='ready') and runs its OnExpire actions. Used by the active timers widget collect button.",
                fr: "Consomme un objet prêt (statut='ready') et exécute ses actions OnExpire. Utilisé par le bouton de collecte du widget timers.",
            },
            when: {
                en: "On the collect button of an active timer row.",
                fr: "Sur le bouton de collecte d'une ligne de timer actif.",
            },
            options: [],
            effect: {
                en: "The item is consumed, its OnExpire chain executes (loot, stat changes, etc.).",
                fr: "L'objet est consommé, sa chaîne OnExpire s'exécute (loot, changements de stats, etc.).",
            },
            related: ["SetTimer", "Countdown"],
        },
    },
    {
        type: "Countdown",
        icon: "/icons/timer.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Countdown", fr: "Compte à rebours" },
            desc: {
                en: "Counts down from the bound 'duration' seconds. Emits 'Tick' every second and 'Finish' when reaching zero. Exposes 'secondsRemaining' as data output for reactive text/visibility bindings.",
                fr: "Compte à rebours depuis 'duration' secondes. Émet 'Tick' chaque seconde et 'Finish' à zéro. Expose 'secondsRemaining' en sortie data pour des bindings réactifs (texte, visibilité).",
            },
            when: {
                en: "On a timer row entry visible event.",
                fr: "Sur l'événement de visibilité d'une ligne de timer.",
            },
            options: [],
            effect: {
                en: "The bound text/visibility consumers update each second; 'Finish' fires once.",
                fr: "Les consommateurs de texte/visibilité se rafraîchissent chaque seconde ; 'Finish' fire une fois.",
            },
            related: [],
        },
    },
    {
        type: "Switch",
        icon: "/icons/branch.svg",
        category: "narrative",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Switch", fr: "Aiguillage" },
            desc: {
                en: "Routes the incoming value to one of its case outputs. Wire a Get → Switch.value, list the case values to match. Each case becomes an output port that fires only when the incoming value matches it. Wire a port onto a downstream gate to apply that branch.",
                fr: "Aiguille la valeur entrante vers une de ses sorties de cas. Branche un Get → Switch.value, liste les valeurs à matcher. Chaque cas devient un port de sortie qui s'active seulement quand la valeur entrante correspond. Branche un port sur un gate aval pour appliquer cette branche.",
            },
            when: {
                en: "Drive type-specific behaviour : on Warning apply a font + a colour, on Error change just the border, etc.",
                fr: "Piloter du comportement par type : sur Warning appliquer une police + une couleur, sur Error changer seulement la bordure, etc.",
            },
            options: [
                {
                    field: "cases",
                    label: { en: "Cases", fr: "Cas" },
                    detail: {
                        en: "List of values to match. Each value becomes an output port.",
                        fr: "Liste des valeurs à matcher. Chaque valeur devient un port de sortie.",
                    },
                },
            ],
            effect: {
                en: "The matching case's port fires (truthy) ; every other port stays nil. The implicit `Default` port fires when no case matches.",
                fr: "Le port du cas qui matche s'active ; tous les autres restent nil. Le port `Default` implicite s'active quand aucun cas ne matche.",
            },
            related: ["Get", "Compare", "Branch"],
        },
    },
    {
        type: "Get",
        icon: "/icons/branch.svg",
        category: "narrative",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Read a value", fr: "Lire une donnée" },
            desc: {
                en: "Reads a value from the player or the project (a stat, an inventory list, the recipe catalogue, etc.).",
                fr: "Lit une valeur du joueur ou du projet (une stat, une liste d'objets, le catalogue de recettes, etc.).",
            },
            when: {
                en: "When you want to display vitality, the inventory list, the active quests, etc.",
                fr: "Quand tu veux afficher la vie, la liste de l'inventaire, les quêtes actives, etc.",
            },
            options: [
                {
                    field: "query",
                    label: { en: "What to read", fr: "Quoi lire" },
                    detail: {
                        en: "Pick the source (player or project catalogue) and which one.",
                        fr: "Choisis la source (joueur ou catalogue projet) et lequel.",
                    },
                },
            ],
            effect: {
                en: "Exposes the read value through the data ports of the source (current/max for stats, items/count for lists, etc.).",
                fr: "Expose la valeur lue via les ports de données de la source (current/max pour les stats, items/count pour les listes, etc.).",
            },
            related: ["Get", "ForEach", "Switch"],
        },
    },
    {
        type: "Math",
        icon: "/icons/branch.svg",
        category: "narrative",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Calculation", fr: "Calcul" },
            desc: {
                en: "Performs a maths operation between two values: add, subtract, multiply, divide…",
                fr: "Fait une opération entre deux valeurs : addition, soustraction, multiplication, division…",
            },
            when: {
                en: "When you want vitality / 10 to get the number of hearts to display.",
                fr: "Quand tu veux faire vitalité / 10 pour obtenir le nombre de cœurs à afficher.",
            },
            options: [
                {
                    field: "operator",
                    label: { en: "Operation", fr: "Opération" },
                    detail: {
                        en: "Add, subtract, multiply, divide, min, max, abs, floor, ceil, round.",
                        fr: "Addition, soustraction, multiplication, division, min, max, valeur absolue, plancher, plafond, arrondi.",
                    },
                },
            ],
            effect: {
                en: "Returns the result through the data port `value`.",
                fr: "Renvoie le résultat via le port de donnée `value`.",
            },
            related: ["Compare", "Get"],
        },
    },
    {
        type: "Concat",
        icon: "/icons/branch.svg",
        category: "narrative",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Compose a text", fr: "Texte composé" },
            desc: {
                en: "Builds a text from several parts: fixed pieces and live values mixed.",
                fr: "Construit un texte à partir de plusieurs morceaux : du fixe et du dynamique mélangés.",
            },
            when: {
                en: "When you want 'Vitality: 80 / 100' with the live numbers inside.",
                fr: "Quand tu veux 'Vitalité : 80 / 100' avec les vrais chiffres dedans.",
            },
            options: [
                {
                    field: "parts",
                    label: { en: "Parts", fr: "Morceaux" },
                    detail: {
                        en: "Add fixed text or wire a live value for each piece, in order.",
                        fr: "Ajoute du texte fixe ou branche une valeur dynamique pour chaque morceau, dans l'ordre.",
                    },
                },
            ],
            effect: {
                en: "Returns the assembled string through the data port `value`.",
                fr: "Renvoie le texte assemblé via le port de donnée `value`.",
            },
            related: ["Get", "ForEach"],
        },
    },
    {
        type: "Filter",
        icon: "/icons/filter.svg",
        category: "interface",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Keep only…", fr: "Garder seulement…" },
            desc: {
                en: "Keeps only the items of a list that match a rule (e.g. category equals 'cooking'). Plug a list on the source input; the output is the filtered list.",
                fr: "Garde seulement les éléments d'une liste qui correspondent à une règle (ex : catégorie égale à 'cooking'). Branche une liste sur l'entrée source ; la sortie est la liste filtrée.",
            },
            when: {
                en: "Between a Get → list source and a ForEach. To narrow recipes by tab, quests by type, items by category.",
                fr: "Entre un Get → liste et un ForEach. Pour filtrer les recettes par onglet, les quêtes par type, les items par catégorie.",
            },
            options: [
                {
                    field: "field",
                    label: { en: "Field", fr: "Champ" },
                    detail: {
                        en: "Name of the field to compare, e.g. 'category'. Use dots to descend: 'outputs.0.itemId'.",
                        fr: "Nom du champ à comparer, ex : 'category'. Utilise des points pour descendre : 'outputs.0.itemId'.",
                    },
                },
                {
                    field: "op",
                    label: { en: "Operator", fr: "Opérateur" },
                    detail: {
                        en: "==, !=, >=, <=, >, <. Strings compare with == / !=; numbers all six.",
                        fr: "==, !=, >=, <=, >, <. Les chaînes utilisent == / != ; les nombres tous les six.",
                    },
                },
            ],
            effect: {
                en: "Outputs the filtered list and its count. An empty value (no tab picked yet) passes the source through unchanged.",
                fr: "Renvoie la liste filtrée et son nombre. Une valeur vide (aucun onglet choisi) laisse passer la source telle quelle.",
            },
            related: ["Get", "ForEach"],
        },
    },
    {
        type: "Group",
        icon: "/icons/filter.svg",
        category: "interface",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Group by…", fr: "Regrouper par…" },
            desc: {
                en: "Groups a list by a field (e.g. all items with the same itemId become one entry with a count). Each output entry is { key, count, first } — first is the first source element of the group, handy for reading shared metadata like name or icon.",
                fr: "Regroupe une liste par un champ (ex : tous les items avec le même itemId deviennent une entrée avec un compteur). Chaque entrée de sortie est { key, count, first } — first est le premier élément du groupe, pratique pour lire le nom ou l'icône partagés.",
            },
            when: {
                en: "Between a Get → list source and a ForEach when you want one card per kind instead of one per instance (picker, inventory).",
                fr: "Entre un Get → liste et un ForEach quand tu veux une carte par sorte plutôt qu'une par instance (sélecteur, inventaire).",
            },
            options: [
                {
                    field: "by",
                    label: { en: "Group by field", fr: "Regrouper par champ" },
                    detail: {
                        en: "Dot-notation path of the field used as group key (e.g. 'itemId'). Leave empty to group a list of scalars by identity.",
                        fr: "Chemin du champ servant de clé de regroupement (ex : 'itemId'). Laisse vide pour regrouper une liste de scalaires par identité.",
                    },
                },
            ],
            effect: {
                en: "Outputs the grouped list and its size.",
                fr: "Renvoie la liste regroupée et sa taille.",
            },
            related: ["Get", "Filter", "ForEach"],
        },
    },
    {
        type: "Flatten",
        icon: "/icons/filter.svg",
        category: "interface",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Flatten by…", fr: "Aplatir par…" },
            desc: {
                en: "Takes a list of entries that each contain a sub-list field, and concatenates all those sub-lists into a single flat list. Useful to iterate inner instances of grouped items.",
                fr: "Prend une liste d'entrées contenant chacune une sous-liste, et concatène toutes ces sous-listes en une seule liste à plat. Utile pour itérer les instances d'items groupés.",
            },
            when: {
                en: "Between a Get(Player.Items)→Filter and a ForEach when you want one card per instance (e.g. Companion grid).",
                fr: "Entre un Get(Player.Items)→Filter et un ForEach quand tu veux une carte par instance (ex : grille Compagnon).",
            },
            options: [
                {
                    field: "by",
                    label: { en: "Sub-list field", fr: "Champ sous-liste" },
                    detail: {
                        en: "Dot-notation path of the sub-list field on each entry (e.g. 'instances').",
                        fr: "Chemin du champ contenant la sous-liste sur chaque entrée (ex : 'instances').",
                    },
                },
            ],
            effect: {
                en: "Outputs the flattened list and its total length.",
                fr: "Renvoie la liste aplatie et sa longueur totale.",
            },
            related: ["Get", "Filter", "ForEach"],
        },
    },
    {
        type: "ResolveItem",
        icon: "/icons/filter.svg",
        category: "interface",
        validContexts: ALL_CONTEXTS,
        requiresPlayer: false,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: false,
        content: {
            name: { en: "Read item details", fr: "Lire les infos d'un objet" },
            desc: {
                en: "Reads the project's item template by id and exposes its name, description and category as separate outputs. All outputs return nil if the id has no template.",
                fr: "Lit le modèle d'objet du projet par id et expose son nom, sa description et sa catégorie comme sorties distinctes. Toutes les sorties renvoient nil si l'id ne correspond à aucun modèle.",
            },
            when: {
                en: "After a ForEach over Player.Items grouped by itemId, to label a card with the item's display name instead of its raw id.",
                fr: "Après un ForEach sur Player.Items regroupé par itemId, pour étiqueter une carte avec le nom de l'objet plutôt que son id brut.",
            },
            options: [],
            effect: {
                en: "Outputs the resolved template fields.",
                fr: "Renvoie les champs du modèle résolu.",
            },
            related: ["Get", "Group", "ForEach"],
        },
    },
    {
        type: "SetState",
        icon: "/icons/uistate-set.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Set UI state", fr: "Mémoriser dans l'UI" },
            desc: {
                en: "Stores a value in the UI's own memory (the active tab, the picked recipe…). Other nodes in the same window can read it with Get → UI state.",
                fr: "Range une valeur dans la mémoire de l'interface (l'onglet actif, la recette choisie…). Les autres nœuds de la fenêtre la lisent avec Get → État UI.",
            },
            when: {
                en: "On a tab button, on a card click — anywhere a click should change what the rest of the window shows.",
                fr: "Sur un bouton d'onglet, sur le clic d'une carte — partout où un clic doit changer ce que le reste de la fenêtre affiche.",
            },
            options: [
                {
                    field: "stateKey",
                    label: { en: "Key", fr: "Clé" },
                    detail: {
                        en: "Free-form name you'll re-use to read the value back (e.g. 'activeTab').",
                        fr: "Nom libre que tu réutiliseras pour relire la valeur (ex : 'activeTab').",
                    },
                },
            ],
            effect: {
                en: "Writes the value under the given key. Any Get(UI state, key) in this window refreshes immediately.",
                fr: "Écrit la valeur sous la clé donnée. Tout Get(État UI, clé) de cette fenêtre se rafraîchit aussitôt.",
            },
            related: ["AppendState", "RemoveState", "Get"],
        },
    },
    {
        type: "AppendState",
        icon: "/icons/uistate-append.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Add to UI state list", fr: "Ajouter à la liste UI" },
            desc: {
                en: "Adds a value to the end of a list held in the UI's own memory (e.g. an item dropped in a free-craft slot).",
                fr: "Ajoute une valeur en fin d'une liste de la mémoire de l'interface (ex : un item déposé dans un slot de craft libre).",
            },
            when: {
                en: "On a 'pick this ingredient' button.",
                fr: "Sur un bouton 'prendre cet ingrédient'.",
            },
            options: [
                {
                    field: "stateKey",
                    label: { en: "Key", fr: "Clé" },
                    detail: {
                        en: "Name of the list to append to. Created on first use.",
                        fr: "Nom de la liste à compléter. Créée à la première utilisation.",
                    },
                },
            ],
            effect: {
                en: "The list grows by one element. Get(UI state, key) refreshes.",
                fr: "La liste grandit d'un élément. Get(État UI, clé) se rafraîchit.",
            },
            related: ["SetState", "RemoveState", "Get"],
        },
    },
    {
        type: "RemoveState",
        icon: "/icons/uistate-remove.svg",
        category: "interface",
        validContexts: PLAYER_CONTEXTS,
        requiresPlayer: true,
        requiresEntity: false,
        requiresSourceItem: false,
        isBlocking: false,
        skippedWhenMissingContext: true,
        content: {
            name: { en: "Remove from UI state list", fr: "Retirer de la liste UI" },
            desc: {
                en: "Removes a value from a list held in the UI's own memory (e.g. removing one item from a craft slot).",
                fr: "Retire une valeur d'une liste de la mémoire de l'interface (ex : enlever un item d'un slot de craft).",
            },
            when: {
                en: "On a small 'X' button on a slot.",
                fr: "Sur un petit bouton 'X' d'un slot.",
            },
            options: [
                {
                    field: "stateKey",
                    label: { en: "Key", fr: "Clé" },
                    detail: {
                        en: "Name of the list to remove from.",
                        fr: "Nom de la liste où retirer la valeur.",
                    },
                },
            ],
            effect: {
                en: "The first matching entry is removed. Get(UI state, key) refreshes.",
                fr: "La première entrée correspondante est retirée. Get(État UI, clé) se rafraîchit.",
            },
            related: ["SetState", "AppendState", "Get"],
        },
    },
] as const;

export const ACTION_META_BY_TYPE = new Map<ActionType, ActionMeta>(
    ACTION_META.map((m) => [m.type, m]),
);


export const findActionMeta = (type: string): ActionMeta | undefined =>
    ACTION_META_BY_TYPE.get(type as ActionType);


export const getActionMeta = (type: ActionType): ActionMeta => {
    const found = findActionMeta(type);
    if (!found) throw new Error(`Missing ACTION_META entry for '${type}'`);
    return found;
};

const ADVANCED_PICKER_TYPES = new Set<ActionType>([
    "Compare",
    "Switch",
    "Get",
    "Math",
    "Concat",
    "Filter",
    "Group",
    "Flatten",
    "ResolveItem",
]);

const INTERNAL_PICKER_TYPES = new Set<ActionType>([
    "TransformRecipeItem",
    "SelectChoice",
    "TransformFreeModeSlots",
    "DeliverQuestItems",
    "SelectTargetItem",
    "ClaimQuestById",
    "SetQuestGoal",
    "UseInventoryItem",
    "CollectTimer",
]);

const wordsFrom = (text: string): string[] =>
    text
        .toLowerCase()
        .split(/[^\p{L}0-9]+/iu)
        .filter((word) => word.length >= 2);

const defaultPickerMeta = (meta: ActionMeta): ActionPickerMeta => {
    const exposure = INTERNAL_PICKER_TYPES.has(meta.type)
        ? "internal"
        : ADVANCED_PICKER_TYPES.has(meta.type)
            ? "advanced"
            : "public";
    return {
        exposure,
        keywords: {
            en: Array.from(new Set([meta.type.toLowerCase(), ...wordsFrom(meta.content.name.en), ...wordsFrom(meta.content.desc.en)])),
            fr: Array.from(new Set([meta.type.toLowerCase(), ...wordsFrom(meta.content.name.fr), ...wordsFrom(meta.content.desc.fr)])),
        },
    };
};

export type PickerActionMeta = ActionMeta & { picker: ActionPickerMeta };

export const getPickerActionMeta = (meta: ActionMeta): PickerActionMeta => ({
    ...meta,
    picker: meta.picker ?? defaultPickerMeta(meta),
});

export const PICKER_ACTION_META: readonly PickerActionMeta[] = ACTION_META.map(getPickerActionMeta);

export const getPickerActions = (): readonly PickerActionMeta[] =>
    PICKER_ACTION_META.filter((meta) => meta.picker.exposure !== "internal" && meta.picker.exposure !== "deprecated");


export const getActionOptionLabel = (
    type: ActionType,
    field: string,
    lang: Lang,
): string => {
    const label = findActionOptionLabel(type, field, lang);
    if (!label) throw new Error(`Missing option '${field}' on ACTION_META entry '${type}'`);
    return label;
};

export const findActionOptionLabel = (
    type: string,
    field: string,
    lang: Lang,
): string | undefined => {
    const meta = findActionMeta(type);
    if (!meta) return undefined;
    const option = meta.content.options.find((o) => o.field === field);
    return option?.label[lang];
};
